// Onda 3b — Tela /financeiro/contas-receber.
// REFACTOR: estrutura quebrada em sub-componentes em ./financeiro-contas-receber/.
// Esse arquivo segue como entry point da rota — mantem a maquina de estado
// principal (parcelas, filtros, drag-and-drop, banners, bulk merge wizard,
// transferencias) e delega os sub-componentes pra ./financeiro-contas-receber/.
import { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { App as AntApp, Select, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { API_URL } from '../config';
import { hojeIso } from '../utils/dataFmt';
import { getToken } from '../utils/auth';
import { pedirDesfazerLiberacao } from '../utils/desfazerLiberacao';
import {
  PRESETS_PERIODO, presetMesAtual, detectarModoNavegador, rotuloNavegador,
  navegarPeriodo,
} from '../utils/periodo';

// Sub-componentes extraidos
import type { Parcela, ColunaKey, DirecaoSort, CampoPeriodo } from './financeiro-contas-receber/types';
import { derivarStatusCR } from './financeiro-contas-receber/types';
import {
  fmt, fmtData, num,
  STATUS_PARC, TIPO_PARCELA_LABEL, TIPO_RECEITA_LABEL,
  COLUNAS_DEFAULT, COLUNAS_SORTAVEIS,
  LS_KEY_ORDEM_COLUNAS, LS_KEY_ORDENACAO, LS_KEY_CR_TIPO_DEFAULT,
  valorSortColuna,
} from './financeiro-contas-receber/utils';
import { KpiCardCR } from './financeiro-contas-receber/KpiCardCR';
import {
  montarFamilias, opcoesDoFiltro, totalDeContratos, separarSelecao,
  parcelaNoFiltro, contratoUnicoDaSelecao, familiaComMaisMembros, valorDaFamilia,
} from './financeiro-contas-receber/filtroContratoFamilia';
import { ModalNovaContaReceber } from './financeiro-contas-receber/ModalNovaContaReceber';
import { ModalLinkBm } from './financeiro-contas-receber/ModalLinkBm';
import { ModalEmitirNf } from './financeiro-contas-receber/ModalEmitirNf';
import { ModalParcela } from './financeiro-contas-receber/ModalParcela';

// Identidade única de conta por parcela (Lucas 2026-08-05, "não é para existir
// dois caixas"): apelido da conta interna quando a FK está carimbada; senão o
// texto cru do CA (legado sem conta interna, ex. Bradesco). Dropdown e filtro
// usam a MESMA função — conta renomeada no CA não duplica mais entrada.
const contaLabel = (p: any) => p.conta_financeira_apelido || p.conta_financeira_ca || '';

// Sprint F11 — Presets de período centralizados em utils/periodo.ts (shared CR/CP).
// Função de retrocompat: helpers locais foram removidos; agora vêm de PRESETS_PERIODO.

export default function FinanceiroContasReceber() {
  return (
    <AntApp>
      <FinanceiroContasReceberInner />
    </AntApp>
  );
}

function FinanceiroContasReceberInner() {
  const { message, modal } = AntApp.useApp();
  // Onda 0 (2026-07-02): deep-links — a URL carrega ?contrato&parcela&situacao.
  // Lê na montagem (inicializa os filtros) e espelha de volta (link copiável).
  const [searchParams, setSearchParams] = useSearchParams();
  const [parcelaUrlPendente, setParcelaUrlPendente] = useState<string | null>(
    () => searchParams.get('parcela'));
  const [parcelas, setParcelas] = useState<Parcela[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // refresh pós-edição não-bloqueante
  const [busca, setBusca] = useState('');
  // Sprint F8.2 — múltiplos status simultâneos (array em vez de string)
  // Sprint F13 — CR abre filtrado por 'pendente' por default. É o foco
  // operacional ("o que tá vencendo / faltando receber"). Usuário pode
  // desmarcar pra ver todos.
  const [filtrosStatus, setFiltrosStatus] = useState<string[]>(() => {
    const s = searchParams.get('situacao');
    if (s && s !== 'liberada') return s.split(',').filter(Boolean);
    return ['pendente'];
  });
  // F-CR-merge-bulk: seleção pra bulk merge das parcelas CA órfãs
  const [selecionadasMerge, setSelecionadasMerge] = useState<Set<string>>(new Set());
  // Agrupar NF (aglutinação FZ 0.5): seleciona parcelas-pai pra faturar em 1 NF única.
  const [modoAgrupar, setModoAgrupar] = useState(false);
  const [selGrupo, setSelGrupo] = useState<Set<string>>(new Set());
  const [agrupando, setAgrupando] = useState(false);
  const [previewBulkMerge, setPreviewBulkMerge] = useState<any>(null);
  const [aplicandoBulk, setAplicandoBulk] = useState(false);
  // F-CR-merge-amb: wizard de revisão das ambíguas
  const [revisandoAmbiguas, setRevisandoAmbiguas] = useState<any>(null);  // { parcelas, indice }
  const [carregandoAmbiguas, setCarregandoAmbiguas] = useState(false);
  // F-CR-transf: conciliação de transferências entre contas
  const [transferenciasModal, setTransferenciasModal] = useState<any>(null);
  const [carregandoTransf, setCarregandoTransf] = useState(false);
  // F-CR-tipo: filtro de tipo de receita. Toggle persistente por usuário.
  // Default: o que estiver salvo em localStorage, senão vazio (todos).
  const [filtrosTipoReceita, setFiltrosTipoReceita] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(LS_KEY_CR_TIPO_DEFAULT);
      if (raw) return JSON.parse(raw);
    } catch {}
    return [];
  });
  const [filtroLiberadas, setFiltroLiberadas] = useState(
    () => searchParams.get('situacao') === 'liberada');
  // Sprint F6 — filtros de período + contrato + preset
  // Sprint F13 — abre em "Este mês" por default (paridade com Contas a Pagar).
  // Antes começava em "— Sem Período —" com navegador desabilitado.
  // Onda 0: deep-link com ?contrato abre com período AMPLO (senão as parcelas
  // do contrato fora do mês corrente somem e o link parece quebrado).
  // Vale igual pro ?parcela: quem vem do Fluxo de Caixa costuma clicar num
  // previsto de outro mês, e o modal só abre se a parcela estiver na lista.
  const deepLinkAmplo = !!(searchParams.get('contrato') || searchParams.get('parcela'));
  const [presetAtivo, setPresetAtivo] = useState<string>(
    () => deepLinkAmplo ? '' : 'Este mês');
  const [periodoIni, setPeriodoIni] = useState<string>(
    () => deepLinkAmplo ? '2019-01-01' : presetMesAtual().ini);
  const [periodoFim, setPeriodoFim] = useState<string>(
    () => deepLinkAmplo ? '2031-12-31' : presetMesAtual().fim);
  // Pedido do Lucas (16/09/2026): "filtrar todos os contratos e aditivos
  // juntos... tenho que poder selecionar mais de um contrato". O filtro virou
  // multi-seleção, e cada valor é OU o id de um contrato OU `fam:<id da raiz>`,
  // que significa "o contrato e todos os aditivos dele".
  const [filtrosContrato, setFiltrosContrato] = useState<string[]>(
    () => (searchParams.get('contrato') || '').split(',').filter(Boolean));
  // Reordenar parcelas por arrasto só faz sentido dentro de UM contrato — com
  // uma família (ou dois contratos) selecionada, a numeração seria de quem?
  const contratoUnico = useMemo(
    () => contratoUnicoDaSelecao(filtrosContrato), [filtrosContrato]);
  const { ids: idsContratoSelecionados, raizes: raizesSelecionadas } = useMemo(
    () => separarSelecao(filtrosContrato), [filtrosContrato]);

  // Filtro por cliente (match exato — opções vêm das próprias linhas).
  // Sentinela '__SEM__' = só parcelas sem cliente vinculado.
  const [filtroCliente, setFiltroCliente] = useState<string>('');
  const [filtroAtrasadas, setFiltroAtrasadas] = useState(false);
  // F-CR-orfas-contrato: filtro pra mostrar so parcelas tipo=contrato SEM
  // contrato_id (FK quebrada — provavel Notion sem vinculo a contrato)
  const [filtroSemContratoLink, setFiltroSemContratoLink] = useState(false);
  // 3 modos de visualização (espelha o que tem em FinanceiroContasPagar):
  //  vencimento (default): filtra por data_vencimento — bate com CA
  //  recebimento: filtra por data_recebimento (regime de caixa estrito)
  //  emissao: filtra por nota_fiscal_emissao (mês fiscal — equiv. competência)
  const [campoPeriodo, setCampoPeriodo] = useState<CampoPeriodo>(() => {
    try {
      const raw = localStorage.getItem('fin_cr_campo_periodo_v2');
      if (raw === 'emissao' || raw === 'vencimento' || raw === 'recebimento') return raw;
    } catch { /* ignore */ }
    return 'vencimento';  // default = espelha CA
  });
  function trocarCampoPeriodo(v: CampoPeriodo) {
    setCampoPeriodo(v);
    try { localStorage.setItem('fin_cr_campo_periodo_v2', v); } catch { /* ignore */ }
  }
  const [hoje] = useState(hojeIso());
  const [mostrarNovaConta, setMostrarNovaConta] = useState(false);
  // Congelamento: mostra/esconde previsões de contratos pausados (caso TIMAC)
  const [mostrarPausados, setMostrarPausados] = useState(false);
  // Sprint F11 — paridade com Contas a Pagar: filtro de conta bancária + Mais filtros.
  const [filtroContaBancaria, setFiltroContaBancaria] = useState<string>('');
  // Chegando por link com contrato na URL, o painel abre: o filtro já está
  // valendo e ficaria escondido atrás do botão "Mais filtros".
  const [mostrarMaisFiltros, setMostrarMaisFiltros] = useState(
    () => !!searchParams.get('contrato'));
  const [pickerCustomAberto, setPickerCustomAberto] = useState(false);  // range picker do navegador

  function aplicarPreset(p: { ini: string; fim: string; rotulo: string }) {
    setPeriodoIni(p.ini); setPeriodoFim(p.fim);
    setPresetAtivo(p.rotulo); setFiltroAtrasadas(false);
  }
  // Sprint F13 — limparPeriodo() removida. Padrão CP/CA: sempre há um período
  // ativo. Pra ver outro recorte, usa-se navegador ◄ ►, presets ou range manual
  // em "Mais filtros". Não há mais estado "— Sem Período —".

  // Sprint F11 — Navegador ◄ ► sobre date range (mesmo widget do Contas a Pagar).
  function _navegar(delta: number) {
    const novo = navegarPeriodo(periodoIni, periodoFim, delta);
    setPeriodoIni(novo.ini); setPeriodoFim(novo.fim); setPresetAtivo('');
  }

  useEffect(() => { carregar(); }, []);

  // Onda 0: espelha os filtros-chave na URL (replace — não polui o histórico).
  // Só grava o que difere do default; limpar o filtro limpa o parâmetro.
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (filtrosContrato.length) p.set('contrato', filtrosContrato.join(',')); else p.delete('contrato');
      const situacao = filtroLiberadas
        ? 'liberada'
        : (filtrosStatus.length > 0 && !(filtrosStatus.length === 1 && filtrosStatus[0] === 'pendente'))
          ? filtrosStatus.join(',')
          : '';
      if (situacao) p.set('situacao', situacao); else p.delete('situacao');
      return p;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosContrato, filtroLiberadas, filtrosStatus]);

  // silent=true: refresh sem bloquear a lista (pós-edição). A lista atual
  // continua visível/interativa e só é substituída quando a resposta chega —
  // evita o "Carregando..." que congelava a tela a cada ação.
  async function carregar(silent = false) {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_listar_contas_receber' },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setParcelas(Array.isArray(d) ? d : []);
    } finally { if (silent) setRefreshing(false); else setLoading(false); }
  }

  async function marcarRecebida(p: Parcela) {
    const valor = window.prompt(
      `Confirma recebimento da parcela ${p.numero_parcela || '—'} (${p.contrato_numero}, ${p.cliente_nome || '?'})?\n\n` +
      `Valor previsto: ${fmt(p.valor_previsto)}\nDigite o valor recebido (default = previsto):`,
      String(p.valor_previsto)
    );
    if (!valor) return;
    const recebido = parseFloat(valor);
    if (isNaN(recebido)) { alert('Valor inválido'); return; }
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_atualizar_conta_receber',
        id: p.id,
        valor_recebido: recebido,
        data_recebimento: hojeIso(),
        status: 'recebida',
      }, { headers: { Authorization: token } });
      carregar(true);
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  // Entradas (recebimentos-filhos, parcela_pai_id preenchido) NUNCA são
  // linhas da lista — elas AGREGAM dentro da parcela prevista (regra Lucas
  // 2026-07-08: cliente pode pagar em N entradas, inclusive parcial; a
  // parcela mostra o acumulado via recebido_efetivo). Sem este filtro, BM
  // consolidado + pagamentos variados somavam duas vezes (caso 25026).
  // Status efetivo por parcela — regra completa extraída pra derivarStatusCR
  // (types.ts) pra aba Financeiro do contrato derivar IGUAL. Inclui o
  // rebaixamento novo: 'atrasada' gravada com vencimento futuro → pendente.
  const parcelasComStatus = useMemo(() => parcelas.filter(p => !p.parcela_pai_id).map(p => {
    const status = derivarStatusCR(p, hoje);
    return status === p.status ? p : { ...p, status };
  }), [parcelas, hoje]);

  // Agrupar NF: quantas parcelas carregadas dividem cada nf_grupo_id (pro selo).
  const nfGrupoContagem = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of parcelas) if (p.nf_grupo_id) m.set(p.nf_grupo_id, (m.get(p.nf_grupo_id) || 0) + 1);
    return m;
  }, [parcelas]);

  // 2026-06-22 (decisão Lucas): parcela RECEBIDA antes de 2024 = histórico/fechado,
  // considerada correta — fica fora do filtro "sem contrato" E do banner laranja.
  const recebidaAntesDe2024 = (p: any) => !!p.data_recebimento && p.data_recebimento < '2024-01-01';
  // "Sem contrato ACIONÁVEL": tipo=contrato sem vínculo, vencimento pós-2024 e
  // não recebida antes de 2024. Banner E filtro usam ESTE mesmo predicado pra não
  // divergirem — pré-2024 (por vencimento OU recebimento) é histórico/intencional.
  const semContratoAcionavel = (p: any) =>
    p.tipo_receita === 'contrato' && !p.contrato_id && !p.contrato_externo &&
    !recebidaAntesDe2024(p) && !!p.data_vencimento && p.data_vencimento >= '2024-01-01';

  // Congelamento (Lucas 2026-07-08, caso TIMAC): contrato PAUSADO esconde as
  // previsões no financeiro — dinheiro que já entrou (recebida/parcial) e NF
  // emitida em aberto continuam visíveis; o resto fica congelado até o
  // contrato voltar. Toggle "⏸ Pausados" exibe.
  const congelada = (p: any) =>
    p.contrato_status === 'pausado' &&
    p.status !== 'recebida' && p.status !== 'parcialmente_recebida' &&
    p.nf_situacao !== 'emitida';
  const qtdCongeladas = parcelasComStatus.filter(congelada).length;

  const filtradas = parcelasComStatus.filter(p => {
    if (!mostrarPausados && congelada(p)) return false;
    if (filtrosStatus.length > 0) {
      if (!filtrosStatus.includes(p.status)) return false;
    } else if (p.status === 'cancelada') {
      // Padrão (nenhum chip de status): canceladas FORA da lista e dos cards.
      // Selecionar o chip "Cancelada" mostra — e aí os cards somam junto
      // (regra Lucas 2026-07-08: cards = soma do que os filtros apresentam).
      return false;
    }
    // F-CR-tipo: filtro de tipo de receita (persistente por usuário)
    if (filtrosTipoReceita.length > 0) {
      // Trata null como 'nao_classificada' pra filtro funcionar mesmo se backfill não cobriu
      const tipoEfetivo = p.tipo_receita || 'nao_classificada';
      if (!filtrosTipoReceita.includes(tipoEfetivo)) return false;
    }
    if (filtroLiberadas && !p.liberada) return false;
    if (filtroAtrasadas && p.status !== 'atrasada') return false;
    if (!parcelaNoFiltro(p, idsContratoSelecionados, raizesSelecionadas)) return false;
    if (filtroCliente) {
      if (filtroCliente === '__SEM__') { if (p.cliente_nome) return false; }
      else if ((p.cliente_nome || '') !== filtroCliente) return false;
    }
    if (filtroContaBancaria && contaLabel(p) !== filtroContaBancaria) return false;
    if (filtroSemContratoLink && !semContratoAcionavel(p)) return false;
    if (periodoIni || periodoFim) {
      // 3 modos:
      //  vencimento (default) → data_vencimento
      //  recebimento (caixa)  → data_recebimento; sem recebimento, mostra
      //                         em-aberto cuja data_vencimento está no range
      //  emissao             → nota_fiscal_emissao (mês fiscal)
      let dataRef = '';
      let dentro = false;
      if (campoPeriodo === 'recebimento') {
        const dRec = (p.data_recebimento || '').slice(0, 10);
        const dVen = (p.data_vencimento || '').slice(0, 10);
        if (dRec) {
          dataRef = dRec;
          dentro = (!periodoIni || dataRef >= periodoIni) && (!periodoFim || dataRef <= periodoFim);
        } else if (dVen) {
          // em-aberto: mostra se vencimento cai no range (projeção)
          dataRef = dVen;
          dentro = (!periodoIni || dataRef >= periodoIni) && (!periodoFim || dataRef <= periodoFim);
        }
        if (!dentro) return false;
      } else if (campoPeriodo === 'emissao') {
        dataRef = (p.nota_fiscal_emissao || '').slice(0, 10);
        // Sem NF emitida, a competência é a data fiscal PREVISTA — vale pra
        // qualquer origem, não só espelho do CA. Antes o fallback era exclusivo
        // do CA e escondia parcela de contrato ainda não faturada: 12 linhas,
        // R$ 98 mil em aberto sumiam do modo Emissão (achado do Lucas no
        // contrato 26051, 2026-07-28). Parcela já faturada segue pela emissão —
        // a regra da casa (competência canônica = nota_fiscal_emissao) não muda.
        if (!dataRef) {
          dataRef = (p.data_competencia || '').slice(0, 10);
        }
        // Espelho antigo do CA sem competência sincronizada: último recurso.
        if (!dataRef && p.origem === 'conta_azul_api') {
          dataRef = (p.data_recebimento || '').slice(0, 10);
        }
        if (!dataRef) return false;
        if (periodoIni && dataRef < periodoIni) return false;
        if (periodoFim && dataRef > periodoFim) return false;
      } else {
        // vencimento (default — espelha CA)
        dataRef = (p.data_vencimento || '').slice(0, 10);
        if (!dataRef) return false;
        if (periodoIni && dataRef < periodoIni) return false;
        if (periodoFim && dataRef > periodoFim) return false;
      }
    }
    if (busca) {
      const q = busca.toLowerCase();
      return (p.contrato_numero || '').toLowerCase().includes(q)
          || (p.contrato_titulo || '').toLowerCase().includes(q)
          || (p.cliente_nome || '').toLowerCase().includes(q)
          || (p.descricao || '').toLowerCase().includes(q)
          || (p.parcela_titulo || '').toLowerCase().includes(q)
          || (p.nota_fiscal_numero || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Sprint F6 → 16/09/2026: a lista do filtro é por FAMÍLIA (contrato-raiz +
  // aditivos), não por contrato solto. O servidor manda a raiz de cada parcela,
  // então a família aparece mesmo quando o contrato-pai não tem parcela nenhuma
  // — caso real (Ceriluz: 3 aditivos com parcelas, pai com zero), em que o pai
  // simplesmente não existia no filtro.
  const familiasDisponiveis = useMemo(() => montarFamilias(parcelas), [parcelas]);
  const opcoesContrato = useMemo(() => opcoesDoFiltro(familiasDisponiveis), [familiasDisponiveis]);
  const totalContratosNoFiltro = useMemo(
    () => totalDeContratos(familiasDisponiveis), [familiasDisponiveis]);
  // Quando há UM contrato escolhido e a família dele tem mais gente, a tela
  // oferece trazer os aditivos num clique — é o caminho de quem chegou pelo
  // link da aba Financeiro do contrato, que aponta para um contrato só.
  const familiaDoUnico = useMemo(
    () => familiaComMaisMembros(filtrosContrato, familiasDisponiveis),
    [filtrosContrato, familiasDisponiveis]);

  // Lista distinct de clientes pro filtro (das próprias linhas — match exato)
  const clientesDisponiveis = useMemo(() => {
    const s = new Set<string>();
    for (const p of parcelas) if (p.cliente_nome) s.add(p.cliente_nome);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [parcelas]);

  // Sprint F11 → 2026-08-05: dropdown SÓ com contas internas (apelido) — as
  // mesmas do Contas & Saldos. Texto cru do CA sem conta interna (Bradesco
  // legado etc.) fica fora do filtro de propósito, decisão do Lucas; essas
  // linhas seguem visíveis em "Todas". (O texto cru nem vinha na listagem —
  // o dropdown estava vazio desde o F11.)
  const contasDisponiveis = useMemo(() => {
    const s = new Set<string>();
    for (const p of parcelas) {
      if (p.conta_financeira_apelido) s.add(p.conta_financeira_apelido);
    }
    return Array.from(s).sort();
  }, [parcelas]);

  // Sprint F8.1 — Sort por clique no header. Default: vencimento ascendente
  // (mais antiga primeiro). Persistido em localStorage por usuário.
  const [ordenacao, setOrdenacao] = useState<{ coluna: ColunaKey; direcao: DirecaoSort }>(() => {
    try {
      const raw = localStorage.getItem(LS_KEY_ORDENACAO);
      if (raw) {
        const p = JSON.parse(raw);
        if (p && COLUNAS_SORTAVEIS.includes(p.coluna) && (p.direcao === 'asc' || p.direcao === 'desc')) {
          return p;
        }
      }
    } catch { /* ignore */ }
    return { coluna: 'emissao' as ColunaKey, direcao: 'desc' as DirecaoSort };
  });
  function aplicarSort(coluna: ColunaKey) {
    if (!COLUNAS_SORTAVEIS.includes(coluna)) return;
    setOrdenacao(prev => {
      const nova = prev.coluna === coluna
        ? { coluna, direcao: (prev.direcao === 'asc' ? 'desc' : 'asc') as DirecaoSort }
        : { coluna, direcao: 'asc' as DirecaoSort };
      try { localStorage.setItem(LS_KEY_ORDENACAO, JSON.stringify(nova)); } catch { /* ignore */ }
      return nova;
    });
  }

  const parcelasExibidas = useMemo(() => {
    const arr = [...filtradas];
    arr.sort((a, b) => {
      const va = valorSortColuna(a, ordenacao.coluna);
      const vb = valorSortColuna(b, ordenacao.coluna);
      let cmp = 0;
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), 'pt-BR');
      return ordenacao.direcao === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtradas, ordenacao]);

  // Drag-and-drop state (só ativo com UM contrato selecionado — ver contratoUnico)
  const [dragId, setDragId] = useState<string | null>(null);
  const [salvandoOrdem, setSalvandoOrdem] = useState(false);
  const [ordemLocal, setOrdemLocal] = useState<Parcela[] | null>(null);

  async function onDropParcela(targetId: string) {
    if (!contratoUnico || !dragId || dragId === targetId) { setDragId(null); return; }
    const lista = ordemLocal || parcelasExibidas;
    const fromIdx = lista.findIndex(p => p.id === dragId);
    const toIdx = lista.findIndex(p => p.id === targetId);
    if (fromIdx < 0 || toIdx < 0) { setDragId(null); return; }
    const nova = [...lista];
    const [moved] = nova.splice(fromIdx, 1);
    nova.splice(toIdx, 0, moved);
    setOrdemLocal(nova);
    setDragId(null);

    // Persiste backend
    setSalvandoOrdem(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_reordenar_parcelas',
        contrato_id: contratoUnico,
        ordem: nova.map((p, idx) => ({ id: p.id, numero_parcela: idx + 1 })),
      }, { headers: { Authorization: token } });
      await carregar(true);
      setOrdemLocal(null);
    } catch (e: any) {
      alert('Erro ao reordenar: ' + (e.response?.data?.error || e.message));
      setOrdemLocal(null);
    } finally { setSalvandoOrdem(false); }
  }

  // Lista efetivamente renderizada: prioriza ordem otimista local durante o save
  const parcelasRender = ordemLocal || parcelasExibidas;

  // Sprint F8 — Reordenação de COLUNAS (persistida em localStorage)
  const [ordemColunas, setOrdemColunas] = useState<ColunaKey[]>(() => {
    try {
      const raw = localStorage.getItem(LS_KEY_ORDEM_COLUNAS);
      if (raw) {
        const parsed = JSON.parse(raw) as ColunaKey[];
        // Valida que só tem chaves conhecidas e que cobre todas (ou completa com default)
        const valid = parsed.filter(k => COLUNAS_DEFAULT.includes(k));
        const missing = COLUNAS_DEFAULT.filter(k => !valid.includes(k));
        return [...valid, ...missing];
      }
    } catch { /* ignore */ }
    return COLUNAS_DEFAULT;
  });
  const [dragColuna, setDragColuna] = useState<ColunaKey | null>(null);

  function salvarOrdemColunas(nova: ColunaKey[]) {
    setOrdemColunas(nova);
    try { localStorage.setItem(LS_KEY_ORDEM_COLUNAS, JSON.stringify(nova)); } catch { /* ignore */ }
  }
  function resetarColunas() {
    setOrdemColunas(COLUNAS_DEFAULT);
    try { localStorage.removeItem(LS_KEY_ORDEM_COLUNAS); } catch { /* ignore */ }
  }
  function onDropColuna(target: ColunaKey) {
    if (!dragColuna || dragColuna === target) { setDragColuna(null); return; }
    const arr = [...ordemColunas];
    const fromIdx = arr.indexOf(dragColuna);
    const toIdx = arr.indexOf(target);
    if (fromIdx < 0 || toIdx < 0) { setDragColuna(null); return; }
    arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, dragColuna);
    salvarOrdemColunas(arr);
    setDragColuna(null);
  }

  // Definições de cada coluna (label, header style, render do td)
  const COL_DEFS: Record<ColunaKey, {
    label: string;
    thStyle: React.CSSProperties;
    render: (p: Parcela) => React.ReactNode;
  }> = {
    contrato: {
      label: 'Contrato',
      thStyle: { padding: 10, textAlign: 'left', fontSize: 11 },
      render: (p) => {
        // Caso 1: contrato cadastrado no operacional (FK)
        if (p.contrato_numero) {
          return (
            <>
              <span style={{ fontFamily: 'monospace', color: '#020D7F' }}>{p.contrato_numero}</span>
              {p.numero_parcela && (
                <span style={{ marginLeft: 6, fontSize: 10, color: '#888', fontFamily: 'sans-serif' }}>
                  #{p.numero_parcela}
                </span>
              )}
            </>
          );
        }
        // Caso 2: contrato externo (texto livre — pre-sistema, antigos)
        if (p.contrato_externo) {
          return (
            <span style={{ fontFamily: 'monospace', color: '#7C3AED' }}
              title="Contrato externo (não cadastrado no Operacional)">
              {p.contrato_externo} <span style={{ fontSize: 9, color: '#aaa' }}>(externo)</span>
            </span>
          );
        }
        return <span style={{ color: '#bbb', fontStyle: 'italic', fontSize: 11 }}>—</span>;
      },
    },
    cliente: {
      label: 'Cliente',
      thStyle: { padding: 10, textAlign: 'left', fontSize: 11 },
      render: (p) => p.cliente_nome || <span style={{ fontStyle: 'italic', color: '#bbb' }}>—</span>,
    },
    parcela: {
      label: 'Parcela',
      thStyle: { padding: 10, textAlign: 'left', fontSize: 11 },
      render: (p) => {
        const tituloPrincipal = p.parcela_titulo || p.descricao;
        // F-CR-tipo: tag inline do tipo de receita (estilo CA)
        const tipoRec = p.tipo_receita && TIPO_RECEITA_LABEL[p.tipo_receita]
          ? TIPO_RECEITA_LABEL[p.tipo_receita] : null;
        return (
          <div title={p.descricao_completa || tituloPrincipal || ''}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              {p.tipo_parcela && TIPO_PARCELA_LABEL[p.tipo_parcela] && (
                <span title={TIPO_PARCELA_LABEL[p.tipo_parcela].label} style={{ fontSize: 11 }}>
                  {TIPO_PARCELA_LABEL[p.tipo_parcela].icon}
                </span>
              )}
              <div style={{ fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                {tituloPrincipal || '—'}
              </div>
              {p.liberada && p.status === 'pendente' && (
                <span title={`Liberada (${p.liberada_por || 'manual'})`}
                  style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3,
                    background: '#F3E8FF', color: '#5B21B6', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  LIBERADA
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3, alignItems: 'center' }}>
              {tipoRec && (
                <span title={`Tipo: ${tipoRec.label}`}
                  style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3,
                    background: tipoRec.bg, color: tipoRec.cor, fontWeight: 600,
                    border: p.tipo_receita === 'nao_classificada' ? '0.5px solid #E68B96' : 'none' }}>
                  {tipoRec.icon} {tipoRec.label}
                </span>
              )}
              {p.codigo_crea && (
                <span style={{ fontSize: 9, color: '#888',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                  CREA {p.codigo_crea.split(' - ')[0]}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    vencimento: {
      label: 'Vencimento',
      thStyle: { padding: 10, textAlign: 'center', fontSize: 11, width: 100 },
      render: (p) => {
        const isAtrasada = p.status === 'atrasada';
        return (
          <span style={{ color: isAtrasada ? '#B91626' : '#888', fontWeight: isAtrasada ? 600 : 400 }}>
            {fmtData(p.data_vencimento)}
          </span>
        );
      },
    },
    // Sprint F8.3 — Emissão (data nota_fiscal_emissao). Mostra vencimento
    // calculado como subtítulo quando há emissão+prazo.
    emissao: {
      label: 'Emissão',
      thStyle: { padding: 10, textAlign: 'center', fontSize: 11, width: 110 },
      render: (p) => {
        const isAtrasada = p.status === 'atrasada';
        const venc = p.data_vencimento ? fmtData(p.data_vencimento) : null;
        // O Conta Azul não expõe data de emissão da NF. Fallback prioriza
        // data_recebimento (mais perto da realidade pra rendimento financeiro
        // sem NF), e usa vencimento só se não tem recebimento. Marca com * pra
        // deixar claro que é aproximação.
        const ehCaSemEmissao = !p.nota_fiscal_emissao && p.origem === 'conta_azul_api';
        const fallbackDate = p.data_recebimento_real || p.data_recebimento || p.data_vencimento;
        const fallbackLabel = p.data_recebimento_real || p.data_recebimento ? 'recebimento' : 'vencimento';
        return (
          <div style={{ textAlign: 'center' }}>
            {p.nota_fiscal_emissao ? (
              <div style={{ color: '#222' }}>{fmtData(p.nota_fiscal_emissao)}</div>
            ) : ehCaSemEmissao && fallbackDate ? (
              <div style={{ color: '#999' }}
                title={`O Conta Azul não informa data de emissão da NF — exibindo ${fallbackLabel} como aproximação.`}>
                {fmtData(fallbackDate)} <span style={{ color: '#E08600', fontWeight: 700 }}>*</span>
              </div>
            ) : p.data_competencia ? (
              // NF ainda não emitida: mostra a competência PREVISTA, que é por
              // onde o filtro de Emissão passa a considerar a linha.
              <div style={{ color: '#999' }}
                title="NF ainda não emitida — competência prevista (é por esta data que o filtro de Emissão considera a parcela).">
                {fmtData(p.data_competencia)} <span style={{ fontSize: 9, color: '#999' }}>prev.</span>
              </div>
            ) : (
              <div style={{ color: '#bbb' }}>—</div>
            )}
            {venc && !ehCaSemEmissao && (
              <div style={{ fontSize: 9,
                color: isAtrasada ? '#B91626' : '#888',
                fontWeight: isAtrasada ? 600 : 400 }}
                title="Vencimento (= emissão + prazo)">
                venc. {venc}
              </div>
            )}
          </div>
        );
      },
    },
    previsto: {
      label: 'Bruto Previsto',
      thStyle: { padding: 10, textAlign: 'right', fontSize: 11, width: 105 },
      render: (p) => {
        const qtdOfx = num(p.qtd_ofx_vinculadas);
        const somaOfx = num(p.soma_ofx_vinculada);
        const liquido = num(p.valor_liquido_previsto) || num(p.valor_previsto);
        const bruto = num(p.valor_previsto);
        // FIN-MATCH-LIQ2: o "fechou" compara com o líquido A RECEBER (líquido
        // − descontos, piso 0) — o backend manda pronto em estado.previsto;
        // fallback local enquanto a resposta não traz o campo.
        const liqAReceber = p.estado?.previsto?.liquido_a_receber != null
          ? num(p.estado.previsto.liquido_a_receber)
          : Math.max(liquido - num(p.descontos), 0);
        const fechou = qtdOfx > 0 && (Math.abs(liqAReceber - somaOfx) <= 0.02 || Math.abs(bruto - somaOfx) <= 0.02);
        const recEfetivo = num(p.recebido_efetivo ?? p.valor_recebido);
        const qtdEntradas = num(p.qtd_recebimentos);
        const descontos = num(p.descontos);
        // FIN-7ajustes (A4): só mostra "líq." quando há dedução REAL (não em
        // parcela sem NF/rendimento, onde líquido = bruto).
        // 🚨 Líquido AUSENTE não é dedução de 100% (Lucas, 17/09/2026). Sem a
        // 1ª condição, `num(null)` virava 0 e a tela anunciava "líq. R$ 0,00"
        // num rendimento de R$ 1,13 já recebido. Não é caso raro: 68 parcelas
        // criadas pela conciliação estão com o líquido em branco em produção —
        // o cálculo da retenção só acontece quando alguém salva a parcela pela
        // tela. Em branco, o certo é não dizer nada.
        const temDeducaoReal = p.valor_liquido_previsto != null
          && p.nf_situacao !== 'dispensada'
          && num(p.valor_liquido_previsto) + 0.01 < bruto;
        // FIN-7ajustes (E3): recebido acima do bruto = frações atribuídas de
        // mais (caso BM11 Profill) → sinaliza em vermelho.
        const excedeBruto = bruto > 0 && recEfetivo > bruto + 0.01;
        return (
          <div style={{ textAlign: 'right', fontWeight: 500 }}>
            {fmt(p.valor_previsto)}
            {temDeducaoReal && (
              <div style={{ fontSize: 9, color: '#888' }}>líq. {fmt(p.valor_liquido_previsto)}</div>
            )}
            {descontos > 0 && (
              <div style={{ fontSize: 9, color: '#B07500' }}
                title={p.descontos_motivo || 'desconto'}>− desc. {fmt(descontos)}</div>
            )}
            {/* Entradas agregadas na parcela (regra Lucas 2026-07-08): mostra o
                acumulado recebido e quantas entradas o compõem — inclusive
                pagamento parcial (acumulado < previsto). */}
            {recEfetivo > 0 && (
              <div style={{ fontSize: 9, marginTop: 2, fontWeight: 600,
                color: excedeBruto ? '#B91626'
                  : recEfetivo + 0.02 >= num(p.valor_previsto) ? '#0a7f4a' : '#B07500' }}
                title={excedeBruto
                  ? `⚠ Recebido (${fmt(recEfetivo)}) MAIOR que o bruto (${fmt(bruto)}) — frações atribuídas de mais; revisar.`
                  : qtdEntradas > 0
                  ? `${qtdEntradas} entrada(s) do CA somando ${fmt(recEfetivo)}`
                  : `recebido registrado manualmente`}>
                {excedeBruto ? '⚠' : '✓'} receb. {fmt(recEfetivo)}{qtdEntradas > 1 ? ` (${qtdEntradas} entradas)` : ''}
              </div>
            )}
            {qtdOfx > 0 && (
              <div style={{ fontSize: 9, marginTop: 2,
                color: fechou ? '#0a7f4a' : '#B07500', fontWeight: 600 }}
                title={`${qtdOfx} transação(ões) OFX vinculada(s) · soma R$ ${somaOfx.toFixed(2)}${fechou ? ' — fechado' : ' — saldo a receber'}`}>
                🔗 {qtdOfx} OFX {fechou ? '✓' : '⚠'}
              </div>
            )}
            {/* FZ 5.2 — banco do recebível: conta da parcela ou, na falta,
                a(s) conta(s) das entradas-filhas (agregadas no backend) */}
            {(p.conta_financeira_apelido || p.contas_filhos) && (
              <div style={{ fontSize: 9, marginTop: 2, color: '#888' }}>
                {'🏦 ' + (p.conta_financeira_apelido || p.contas_filhos)}
              </div>
            )}
          </div>
        );
      },
    },
    bm: {
      label: 'BM',
      thStyle: { padding: 10, textAlign: 'center', fontSize: 11, width: 110 },
      render: (p) => (
        <div style={{ textAlign: 'center', fontSize: 10 }}>
          {p.bm_status === 'assinado_cliente' ? (
            <span style={{ color: '#0a7f4a', fontWeight: 600 }} title={`Assinado por ${p.bm_assinado_por_nome || '?'}`}>
              ✓ Assinado
            </span>
          ) : p.bm_status === 'gerado' ? (
            <span style={{ color: '#7C3AED', fontWeight: 600 }}>📋 Gerado</span>
          ) : (
            <span style={{ color: '#bbb' }}>—</span>
          )}
        </div>
      ),
    },
    status: {
      label: 'Status',
      thStyle: { padding: 10, textAlign: 'center', fontSize: 11, width: 95 },
      render: (p) => {
        const sp = STATUS_PARC[p.status] || STATUS_PARC.pendente;
        return (
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 3, fontWeight: 600,
              background: sp.bg, color: sp.cor }}>{sp.label}</span>
          </div>
        );
      },
    },
    nf: {
      label: 'NF',
      thStyle: { padding: 10, textAlign: 'left', fontSize: 11, width: 90 },
      render: (p) => (
        <div style={{ fontSize: 11, color: '#666' }}>
          {p.nota_fiscal_numero || '—'}
          {p.nota_fiscal_emissao && (
            <div style={{ fontSize: 9, color: '#888' }}>{fmtData(p.nota_fiscal_emissao)}</div>
          )}
          {p.nf_grupo_id && (
            <div title="Fatura junto com outras parcelas numa NF única (aglutinada)"
              style={{ marginTop: 2, display: 'inline-block', fontSize: 9, fontWeight: 700,
                color: '#0a7f4a', background: '#E8F5EE', border: '0.5px solid #0a7f4a',
                borderRadius: 4, padding: '1px 5px' }}>
              🔗 NF conjunta{nfGrupoContagem.get(p.nf_grupo_id) ? ` (${nfGrupoContagem.get(p.nf_grupo_id)})` : ''}
            </div>
          )}
        </div>
      ),
    },
    origem: {
      label: 'Origem',
      thStyle: { padding: 10, textAlign: 'center', fontSize: 11, width: 90 },
      render: (p) => {
        // Sprint F15 — Se a parcela está gerenciada no sistema, sobrescreve a
        // exibição de origem com o badge 🔒 (independente da fonte original).
        // Indica visualmente que a reconciliação Notion→CR não vai mais tocar nela.
        if (p.gerenciado_no_sistema) {
          return (
            <div style={{ textAlign: 'center' }}>
              <span title="Gerenciada no sistema — o sistema é a fonte de verdade desta parcela"
                style={{ fontSize: 9, padding: '2px 7px', borderRadius: 3, fontWeight: 600,
                  background: '#FFF8E8', color: '#7A5500' }}>
                🔒 Sistema
              </span>
              {p.origem === 'notion' && (
                <div style={{ fontSize: 8, color: '#888', marginTop: 2 }}
                  title="Originalmente importada do Notion">
                  ex-Notion
                </div>
              )}
            </div>
          );
        }
        // Auditoria: de onde a parcela veio. notion = planejamento;
        // conta_azul_api = sync CA (realizado); sistema = criada manual na UI.
        const m = p.origem === 'conta_azul_api'
          ? { bg: '#E8F5EE', cor: '#0a7f4a', label: '🔗 CA', title: 'Sincronizada do Conta Azul' }
          : p.origem === 'notion'
          ? { bg: '#F3E8FF', cor: '#7C3AED', label: '📋 Notion', title: 'Notion (histórico) — importada do Notion na migração; não sincroniza mais. Edite direto no sistema.' }
          : p.origem === 'sistema'
          ? { bg: '#EFF3FF', cor: '#0930D0', label: '✍ Sistema', title: 'Criada manualmente no sistema' }
          : { bg: '#F2F2F4', cor: '#888', label: '—', title: 'Origem não identificada' };
        return (
          <div style={{ textAlign: 'center' }}>
            <span title={m.title}
              style={{ fontSize: 9, padding: '2px 7px', borderRadius: 3, fontWeight: 600,
                background: m.bg, color: m.cor }}>
              {m.label}
            </span>
            {p.id_externo_ca && p.notion_page_id && (
              <div style={{ fontSize: 8, color: '#0a7f4a', marginTop: 2 }} title="Conciliada Notion + CA">
                ✓ merge
              </div>
            )}
          </div>
        );
      },
    },
    acao: {
      label: 'Ação',
      thStyle: { padding: 10, textAlign: 'center', fontSize: 11, width: 130 },
      render: (p) => (
        <div style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
          {p.status === 'pendente' && !p.liberada && !p.bm_link_hash && (
            <>
              <button onClick={() => gerarBm(p)}
                title="Gerar BM e link público pro cliente assinar"
                style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600,
                  background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 3,
                  cursor: 'pointer', marginRight: 4 }}>
                📐 Gerar BM
              </button>
              <button onClick={() => liberar(p)}
                title="Liberar direto sem BM (parcelas administrativas como aprovação/êxito)"
                style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600,
                  background: 'none', border: '0.5px solid #B07500', color: '#B07500',
                  borderRadius: 3, cursor: 'pointer' }}>
                🚀 Liberar
              </button>
            </>
          )}
          {p.status === 'pendente' && !p.liberada && p.bm_link_hash && p.bm_status === 'gerado' && (
            <>
              <button onClick={() => mostrarLinkBm(p)}
                title="Mostrar link do BM pra reenviar ao cliente"
                style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600,
                  background: 'none', border: '0.5px solid #7C3AED', color: '#7C3AED',
                  borderRadius: 3, cursor: 'pointer', marginRight: 4 }}>
                🔗 Link
              </button>
              <button onClick={() => liberar(p)}
                title="Liberação manual (sem aguardar assinatura do cliente)"
                style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600,
                  background: 'none', border: '0.5px solid #B07500', color: '#B07500',
                  borderRadius: 3, cursor: 'pointer' }}>
                🚀 Liberar
              </button>
            </>
          )}
          {/* Liberou por engano: desfaz E avisa quem emite a nota (30/09/2026). */}
          {p.status === 'pendente' && p.liberada && !p.nota_fiscal_numero && (
            <button onClick={() => pedirDesfazerLiberacao({
                modal, message, parcela: p, onFeito: () => carregar(true),
              })}
              title="Desfazer a liberação e avisar quem emite a NF"
              style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600,
                background: '#fff', color: '#B91626', border: '1px solid #B91626',
                borderRadius: 3, cursor: 'pointer' }}>
              ↩ Desfazer
            </button>
          )}
          {p.status === 'pendente' && p.liberada && (
            <button onClick={() => setEmitindoNf(p)}
              title="Emitir nota fiscal nesta parcela"
              style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600,
                background: '#0930D0', color: '#fff', border: 'none', borderRadius: 3,
                cursor: 'pointer' }}>
              🧾 Emitir NF
            </button>
          )}
          {(p.status === 'faturada' || p.status === 'atrasada' || p.status === 'parcialmente_recebida') && (
            <button onClick={() => marcarRecebida(p)}
              title="Registrar recebimento"
              style={{ padding: '3px 9px', fontSize: 10, fontWeight: 600,
                background: p.status === 'atrasada' ? '#B91626' : '#0a7f4a',
                color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}>
              ✓ Receber
            </button>
          )}
        </div>
      ),
    },
  };

  // F-CR-tipo: total de parcelas sem classificação (todas, ignorando filtros).
  // Banner avisa Marina pra revisar quando há pendentes.
  const naoClassificadasCount = parcelasComStatus.filter(p =>
    (p.tipo_receita || 'nao_classificada') === 'nao_classificada'
  ).length;
  // F-CR-orfas-contrato: o banner laranja "sem contrato vinculado" foi removido
  // (2026-06-22) — o resíduo restante eram contratos antigos já recebidos/cancelados,
  // não acionáveis. semContratoAcionavel fica porque o filtro filtroSemContratoLink
  // ainda usa (caso futuro queira reativar a visão).

  // F-CR-merge-bulk: helpers
  function caOrfaSelecionavel(p: Parcela): boolean {
    return !!p.id_externo_ca && !p.contrato_id;
  }
  function toggleSelecaoMerge(id: string) {
    setSelecionadasMerge(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }
  function selecionarTodasOrfas() {
    const ids = filtradas.filter(caOrfaSelecionavel).map(p => p.id);
    setSelecionadasMerge(new Set(ids));
  }
  function limparSelecaoMerge() { setSelecionadasMerge(new Set()); }

  // Agrupar NF (aglutinação): parcela-pai não cancelada pode entrar num grupo NF.
  function podeAgruparNf(p: Parcela): boolean {
    return !p.parcela_pai_id && p.status !== 'cancelada';
  }
  function toggleSelGrupo(id: string) {
    setSelGrupo(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  async function agruparNf() {
    const ids = Array.from(selGrupo);
    if (ids.length < 2) { alert('Selecione ao menos 2 parcelas pra agrupar.'); return; }
    if (!window.confirm(`Agrupar ${ids.length} parcelas numa NF única?\n\nElas continuam separadas (cada uma com seu valor e rateio), mas faturam juntas em 1 nota. Pode incluir aditivos/contratos diferentes.`)) return;
    setAgrupando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_agrupar_nf', parcela_ids: ids },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      alert(`✓ ${d.parcelas} parcelas agrupadas em 1 NF.${(d.avisos && d.avisos.length) ? '\n\n' + d.avisos.join('\n') : ''}`);
      setSelGrupo(new Set()); setModoAgrupar(false);
      carregar(true);
    } catch (e: any) {
      alert('Erro ao agrupar: ' + (e.response?.data?.error || e.response?.data?.erro || e.message));
    } finally { setAgrupando(false); }
  }
  async function desagruparNf(gruposIds: string[]) {
    if (!gruposIds.length) return;
    if (!window.confirm(`Desagrupar ${gruposIds.length} grupo(s) de NF? As parcelas voltam a faturar separadas.`)) return;
    setAgrupando(true);
    try {
      const token = await getToken();
      for (const g of gruposIds) {
        await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'fin_desagrupar_nf', nf_grupo_id: g },
          { headers: { Authorization: token } });
      }
      alert('✓ Desagrupado. As parcelas voltam a faturar separadas.');
      setSelGrupo(new Set()); setModoAgrupar(false);
      carregar(true);
    } catch (e: any) {
      alert('Erro ao desagrupar: ' + (e.response?.data?.error || e.response?.data?.erro || e.message));
    } finally { setAgrupando(false); }
  }

  async function preverBulkMerge() {
    if (!selecionadasMerge.size) return;
    setAplicandoBulk(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_bulk_mesclar_cr', ids: Array.from(selecionadasMerge), dry_run: true },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setPreviewBulkMerge(d);
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setAplicandoBulk(false); }
  }

  // F-CR-merge-bulk: aplica em chunks de 50 pra evitar timeout API Gateway (29s).
  // Cada merge faz ~5 queries (~150ms), então 50 merges = ~7.5s por chunk.
  const [progressoBulk, setProgressoBulk] = useState<string>('');
  // F-CR-transf: abre modal de conciliação de transferências
  async function abrirTransferenciasModal() {
    setCarregandoTransf(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_listar_transferencias_pendentes' },
        { headers: { Authorization: token }, timeout: 30000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setTransferenciasModal(d);
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setCarregandoTransf(false); }
  }

  async function vincularTransferencia(crId: string, cpId: string) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_vincular_transferencia', cr_id: crId, cp_id: cpId },
        { headers: { Authorization: token } });
      abrirTransferenciasModal();  // refresh
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function desvincularTransferencia(crId: string) {
    if (!window.confirm('Remover o vínculo dessa transferência?')) return;
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_desvincular_transferencia', cr_id: crId },
        { headers: { Authorization: token } });
      abrirTransferenciasModal();  // refresh
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  // F-CR-merge-amb: abre wizard de revisão das ambíguas
  async function abrirRevisaoAmbiguas() {
    setCarregandoAmbiguas(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_listar_cr_ambiguas' },
        { headers: { Authorization: token }, timeout: 60000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (!d.parcelas?.length) {
        alert('Nenhuma parcela ambígua encontrada.');
        return;
      }
      setRevisandoAmbiguas({ parcelas: d.parcelas, indice: 0 });
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setCarregandoAmbiguas(false); }
  }

  async function aplicarMergeAmbigua(caId: string, sistemaId: string) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_mesclar_cr', ca_id: caId, sistema_id: sistemaId },
        { headers: { Authorization: token } });
      proxAmbigua();
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  function proxAmbigua() {
    if (!revisandoAmbiguas) return;
    const proxIdx = revisandoAmbiguas.indice + 1;
    if (proxIdx >= revisandoAmbiguas.parcelas.length) {
      setRevisandoAmbiguas(null);
      carregar(true);  // recarrega a lista pra ver mudanças
      alert(`Revisão concluída — ${revisandoAmbiguas.parcelas.length} ambíguas processadas.`);
    } else {
      setRevisandoAmbiguas({ ...revisandoAmbiguas, indice: proxIdx });
    }
  }

  async function aplicarBulkMerge() {
    if (!previewBulkMerge) return;
    const allIds = Array.from(selecionadasMerge);
    const CHUNK_SIZE = 50;
    const totalChunks = Math.ceil(allIds.length / CHUNK_SIZE);
    setAplicandoBulk(true);
    setProgressoBulk(`0 / ${allIds.length}`);
    let totMescladas = 0, totAmbiguas = 0, totSemCand = 0, totErros = 0;
    let processadas = 0;
    try {
      const token = await getToken();
      for (let i = 0; i < allIds.length; i += CHUNK_SIZE) {
        const chunk = allIds.slice(i, i + CHUNK_SIZE);
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'fin_bulk_mesclar_cr', ids: chunk, dry_run: false },
          { headers: { Authorization: token }, timeout: 28000 });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        totMescladas += d.mescladas || 0;
        totAmbiguas += d.ambiguas || 0;
        totSemCand += d.sem_candidato || 0;
        totErros += d.erros || 0;
        processadas += chunk.length;
        setProgressoBulk(`${processadas} / ${allIds.length} (chunk ${Math.floor(i / CHUNK_SIZE) + 1}/${totalChunks})`);
      }
      alert(
        `✓ Concluído em ${totalChunks} chunk${totalChunks !== 1 ? 's' : ''}\n\n` +
        `${totMescladas} mescladas\n${totAmbiguas} ambíguas\n${totSemCand} sem candidato\n${totErros} erros`
      );
      setPreviewBulkMerge(null);
      limparSelecaoMerge();
      carregar(true);
    } catch (e: any) {
      alert(
        `Processadas ${processadas} de ${allIds.length} antes do erro.\n` +
        `Parciais: ${totMescladas} mescladas, ${totAmbiguas} ambíguas, ${totSemCand} sem candidato.\n\n` +
        `Erro: ${e.response?.data?.error || e.message}`
      );
    } finally {
      setAplicandoBulk(false);
      setProgressoBulk('');
    }
  }
  const liberadasCount = parcelasComStatus.filter(p => p.liberada && p.status === 'pendente').length;
  const liberadasValor = parcelasComStatus.filter(p => p.liberada && p.status === 'pendente')
    .reduce((s, p) => s + (num(p.valor_previsto) - num(p.recebido_efetivo ?? p.valor_recebido)), 0);

  async function liberar(p: Parcela) {
    if (!window.confirm(
      `Liberar pra faturamento a parcela "${p.descricao || p.parcela_titulo}" (${fmt(p.valor_previsto)})?\n\n` +
      `O gestor financeiro poderá emitir NF mesmo sem entrega aprovada.`
    )) return;
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_liberar_parcela', id: p.id,
      }, { headers: { Authorization: token } });
      carregar(true);
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  // Onda 5: estado pra modal de emitir NF
  const [emitindoNf, setEmitindoNf] = useState<Parcela | null>(null);
  // Onda 5b: link BM gerado (pra copiar e enviar pro cliente)
  const [linkBm, setLinkBm] = useState<{ url: string; nome: string } | null>(null);
  // Sprint F1: modal rico de detalhe da parcela
  const [parcelaAberta, setParcelaAberta] = useState<Parcela | null>(null);
  // 2026-09-02: aberta pelo sino (menção em comentário) → cai direto na aba
  // Histórico, onde o comentário mora.
  const [abaInicialModal, setAbaInicialModal] = useState<'historico' | null>(null);

  // Onda 0: ?parcela=<uuid> na URL abre o modal de detalhe assim que a lista
  // carrega (deep-link de outras telas, ex. AbaFinanceiro do contrato).
  // 2026-09-02: o SINO também abre parcela (menção em comentário) — via
  // sessionStorage['cr_abrir_parcela_id'], gravado pelo App.tsx — e o modal
  // abre direto na aba Histórico (o comentário mora lá). A LISTA aqui é o
  // fetch COMPLETO (sem filtro de servidor; status/período são filtros de
  // tela), então id que não está nela ≈ parcela excluída: a chave é consumida
  // com um aviso, em vez de ficar presa pra sempre no sessionStorage.
  useEffect(() => {
    if (loading) return;
    const doSino = sessionStorage.getItem('cr_abrir_parcela_id');
    const alvoId = parcelaUrlPendente || doSino;
    if (!alvoId) return;
    const alvo = parcelas.find(x => x.id === alvoId);
    if (alvo) {
      if (doSino === alvoId) {
        sessionStorage.removeItem('cr_abrir_parcela_id');
        setAbaInicialModal('historico');
      }
      setParcelaAberta(alvo);
    } else if (doSino === alvoId) {
      sessionStorage.removeItem('cr_abrir_parcela_id');
      alert('A parcela desta menção não existe mais (pode ter sido excluída).');
    }
    if (parcelaUrlPendente) setParcelaUrlPendente(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Onda 0: fechar o modal limpa o ?parcela da URL (refresh não re-abre).
  function fecharModalParcela() {
    setParcelaAberta(null);
    setAbaInicialModal(null);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete('parcela');
      return p;
    }, { replace: true });
  }

  async function gerarBm(p: Parcela) {
    if (!window.confirm(
      `Gerar BM (Boletim de Medição) pra parcela "${p.descricao || p.parcela_titulo}" (${fmt(p.valor_previsto)})?\n\n` +
      `Será gerado um link público que você compartilha com o cliente pra ele assinar digitalmente. ` +
      `Após assinatura, a parcela vira liberada pra emissão de NF.`
    )) return;
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'op_gerar_bm', parcela_id: p.id,
      }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setLinkBm({
        url: `${window.location.origin}${d.url_publica}`,
        nome: p.descricao || p.parcela_titulo || '',
      });
      carregar(true);
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  function mostrarLinkBm(p: Parcela) {
    if (!p.bm_link_hash) return;
    setLinkBm({
      url: `${window.location.origin}/publico/bm/${p.bm_link_hash}`,
      nome: p.descricao || p.parcela_titulo || '',
    });
  }

  // Sprint F13 — KPIs sempre refletem o que está visível na tabela (filtradas).
  // Antes tinha um `temFiltroAtivo ? filtradas : parcelasComStatus` que
  // ignorava busca/liberadas quando eram os únicos filtros ativos. Agora bate
  // 100% com o que o usuário enxerga (consistente com Contas a Pagar).
  // Regra (Lucas 2026-07-08): os cards somam EXATAMENTE o que os filtros
  // apresentam na lista — nem mais, nem menos. Canceladas ficam fora por
  // padrão via filtro (acima); selecionar o chip "Cancelada" traz pra ambos.
  const baseKpi = filtradas;

  // Recebido EFETIVO da parcela: soma das entradas conciliadas (filhos do CA)
  // quando existem; senão o valor_recebido manual. Sem isso, parcela
  // conciliada (BM da cascata etc.) aparecia como "recebido 0".
  const recebidoDe = (p: any) => num(p.recebido_efetivo ?? p.valor_recebido);

  const totalRecebido = baseKpi.reduce((s, p) => s + recebidoDe(p), 0);
  const qtdRecebido = baseKpi.filter(p => recebidoDe(p) > 0).length;
  const totalAtrasadas = baseKpi.filter(p => p.status === 'atrasada')
    .reduce((s, p) => s + (num(p.valor_previsto) - recebidoDe(p)), 0);
  const qtdAtrasadas = baseKpi.filter(p => p.status === 'atrasada').length;

  // KPIs CA-style: Atrasadas, Vencem hoje, A vencer, Recebidas, Total
  const vencemHoje = baseKpi.filter(p =>
    p.status !== 'recebida' && p.status !== 'cancelada' &&
    p.data_vencimento === hoje
  );
  const totalVencemHoje = vencemHoje.reduce((s, p) => s + (num(p.valor_previsto) - recebidoDe(p)), 0);
  const aVencer = baseKpi.filter(p =>
    p.status !== 'recebida' && p.status !== 'cancelada' && p.status !== 'atrasada' &&
    p.data_vencimento > hoje
  );
  const totalAVencer = aVencer.reduce((s, p) => s + (num(p.valor_previsto) - recebidoDe(p)), 0);
  const totalPeriodo = baseKpi.reduce((s, p) => s + num(p.valor_previsto), 0);

  return (
    // FIN-UI (2026-07-14): sem maxWidth — aproveita a tela inteira
    <div style={{ padding: '12px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: '#020D7F' }}>📥 Contas a Receber</h2>
        <span style={{ fontSize: 12, color: '#888' }}>
          {parcelasComStatus.length} parcela(s) · {filtradas.length} visíveis
        </span>
        {refreshing && (
          <span style={{ fontSize: 11, color: '#5B7CFF', fontWeight: 600 }}>⟳ atualizando…</span>
        )}
        {/* F-CR-transf: conciliar transferências entre contas */}
        <button onClick={abrirTransferenciasModal} disabled={carregandoTransf}
          style={{ marginLeft: 'auto', padding: '7px 14px', fontSize: 12, fontWeight: 600,
            background: '#fff', color: '#5B7CFF', border: '1px solid #5B7CFF',
            borderRadius: 5, cursor: carregandoTransf ? 'wait' : 'pointer' }}
          title="Vincular CR (entrada) com CP (saída) pra transferências entre contas próprias">
          {carregandoTransf ? 'Carregando…' : '🔄 Conciliar transferências'}
        </button>
        <button onClick={() => { setModoAgrupar(m => !m); setSelGrupo(new Set()); }}
          style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600,
            background: modoAgrupar ? '#0a7f4a' : '#fff', color: modoAgrupar ? '#fff' : '#0a7f4a',
            border: '1px solid #0a7f4a', borderRadius: 5, cursor: 'pointer' }}
          title="Selecionar parcelas pra faturar juntas numa NF única (aglutinar)">
          {modoAgrupar ? '✕ Sair do agrupar' : '🔗 Agrupar em 1 NF'}
        </button>
        <button onClick={() => setMostrarNovaConta(true)}
          style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600,
            background: '#020D7F', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' }}>
          + Nova conta a receber
        </button>
      </div>

      {/* F-CR-tipo: Banner pra parcelas não-classificadas (Marina precisa revisar).
          Aparece quando há pendentes E o filtro atual NÃO está focado nelas. */}
      {naoClassificadasCount > 0 && !filtrosTipoReceita.includes('nao_classificada') && (
        // Minimalista (Lucas 2026-07-08): uma linha só — na transição isso é
        // pendência passageira, não merece um cartaz de 4 botões empilhados.
        <div style={{
          background: '#FDF6F6', border: '0.5px solid #EBC5CB', borderRadius: 6,
          padding: '5px 12px', marginBottom: 12, display: 'flex', alignItems: 'center',
          gap: 12, fontSize: 12, flexWrap: 'wrap',
        }}>
          <span style={{ color: '#B91626', fontWeight: 600, whiteSpace: 'nowrap' }}>
            ⚠ {naoClassificadasCount} sem classificação de receita
          </span>
          <span style={{ color: '#9A6B72', flex: 1, minWidth: 120, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            vieram do CA sem vínculo a contrato
          </span>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => {
              // Limpa filtros que escondem as não-classificadas + foca nelas
              setFiltrosStatus([]);     // mostra todos os status
              setFiltrosTipoReceita(['nao_classificada']);
              try { localStorage.setItem(LS_KEY_CR_TIPO_DEFAULT, JSON.stringify(['nao_classificada'])); } catch {}
              // Expande período pra cobrir histórico + futuras
              setPeriodoIni('2019-01-01');
              setPeriodoFim('2030-12-31');
              setPresetAtivo('');
              // F-CR-tipo: força modo "Vencimento" — emissão exclui CA (que não tem
              // data_emissao da NF), e recebimento exclui pendentes.
              trocarCampoPeriodo('vencimento');
              // Limpa filtros TEXTUAIS que podem esconder o resultado
              setBusca('');
              setFiltrosContrato([]);
              setFiltroCliente('');
              setFiltroContaBancaria('');
            }}
              style={{ padding: 0, fontSize: 12, fontWeight: 600,
                background: 'transparent', color: '#B91626', border: 'none',
                textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Ver todas
            </button>
            {/* Atribuir cliente em massa: pega parcelas CA orfas (cliente_id
                NULL mas com nome em origem_ref->cliente_ca->nome) e tenta
                casar com clientes cadastrados pelo nome. Match unico atribui. */}
            <button onClick={async () => {
              if (!window.confirm(
                'Tentar atribuir cliente cadastrado às parcelas órfãs do CA?\n\nProcura pelo nome do cliente no Conta Azul (origem_ref->cliente_ca->nome) e tenta casar com clientes cadastrados no sistema. Match único atribui automaticamente; ambíguos (2+) ficam pra revisão manual.'
              )) return;
              try {
                const token = await getToken();
                const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
                  { action: 'fin_atribuir_cliente_ca_em_massa' },
                  { headers: { Authorization: token } });
                const d = r.data.body ? JSON.parse(r.data.body) : r.data;
                if (d.atualizadas > 0) {
                  alert(
                    `✓ ${d.atualizadas} parcela(s) com cliente atribuído.\n` +
                    `⚠ ${d.ambiguas} ambíguas (2+ candidatos — revisar manual).\n` +
                    `✗ ${d.sem_match} sem cliente cadastrado correspondente.\n\n` +
                    `Exemplos atribuídos:\n${(d.exemplos || []).map((e: any) =>
                      `  • "${e.nome_ca}" → ${e.cliente_atribuido}`).join('\n')}`
                  );
                  carregar(true);
                } else {
                  alert(`Nenhum cliente atribuído.\n${d.ambiguas} ambíguas · ${d.sem_match} sem match.`);
                }
              } catch (e: any) {
                alert('Erro: ' + (e.response?.data?.error || e.message));
              }
            }}
              style={{ padding: 0, fontSize: 12, fontWeight: 600,
                background: 'transparent', color: '#0930D0', border: 'none',
                textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap' }}
              title="Atribui cliente_id baseado em origem_ref->cliente_ca->nome dos cadastros existentes">
              Atribuir cliente CA
            </button>

            {/* Auto-classificar rendimentos: detecta "REND PAGO APLIC",
                "Rendimentos", "CDB" etc e marca como receita_financeira em batch */}
            <button onClick={async () => {
              if (!window.confirm(
                'Auto-classificar como "Receita financeira" todas as parcelas Não classificadas cuja descrição indica rendimento de aplicação (REND, Rendimentos, CDB, LCI, etc)?\n\nIsso vai limpar a maior parte da lista sem revisão individual.'
              )) return;
              try {
                const token = await getToken();
                const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
                  { action: 'fin_auto_classificar_receita_financeira' },
                  { headers: { Authorization: token } });
                const d = r.data.body ? JSON.parse(r.data.body) : r.data;
                if (d.atualizadas > 0) {
                  alert(`${d.atualizadas} parcela(s) classificadas como Receita financeira.\n\nExemplos:\n${(d.exemplos || []).map((e: any) => `  • ${e.desc} — R$ ${e.valor.toFixed(2)}`).join('\n')}`);
                  carregar(true);
                } else {
                  alert('Nenhuma parcela com padrão de rendimento financeiro encontrada nas Não classificadas.');
                }
              } catch (e: any) {
                alert('Erro: ' + (e.response?.data?.error || e.message));
              }
            }}
              style={{ padding: 0, fontSize: 12, fontWeight: 600,
                background: 'transparent', color: '#7C3AED', border: 'none',
                textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap' }}
              title="Detecta rendimentos de aplicação (REND, Rendimentos, CDB, LCI) pela descrição e classifica em massa">
              Auto-classif.
            </button>
            {/* F-CR-merge-amb: botão pra abrir wizard de revisão das ambíguas */}
            <button onClick={abrirRevisaoAmbiguas} disabled={carregandoAmbiguas}
              title="Revisar parcelas com 2+ candidatos de merge"
              style={{ padding: 0, fontSize: 12, fontWeight: 600,
                background: 'transparent', color: '#B91626', border: 'none',
                textDecoration: 'underline', whiteSpace: 'nowrap',
                cursor: carregandoAmbiguas ? 'wait' : 'pointer' }}>
              {carregandoAmbiguas ? 'Carregando…' : 'Ambíguas'}
            </button>
          </div>
        </div>
      )}

      {filtroSemContratoLink && (
        <div style={{ background: '#FFF8E8', border: '0.5px dashed #E0B84A',
          borderRadius: 4, padding: '6px 12px', marginBottom: 10,
          fontSize: 11, color: '#7C5800', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center' }}>
          <span>🔍 Filtro ativo: parcelas tipo=Contrato SEM contrato vinculado</span>
          <button onClick={() => setFiltroSemContratoLink(false)}
            style={{ background: 'none', border: 'none', color: '#7C5800',
              cursor: 'pointer', fontWeight: 600, fontSize: 11 }}>
            ✕ Limpar filtro
          </button>
        </div>
      )}

      {/* F-CR-transf: modal de conciliação de transferências entre contas.
          FIN-UI (2026-07-14): portalizado pro body (fixed nas Tabs do hub caía pra baixo). */}
      {transferenciasModal && createPortal(
        <div onClick={() => setTransferenciasModal(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 8, padding: 24, maxWidth: 1000,
            width: '95%', maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 16, color: '#020D7F' }}>
                🔄 Conciliação de Transferências entre Contas
              </h3>
              <button onClick={() => setTransferenciasModal(null)}
                style={{ marginLeft: 'auto', background: 'transparent', border: 'none',
                  fontSize: 20, color: '#888', cursor: 'pointer' }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 14 }}>
              <div style={{ padding: 8, background: '#F2F2F4', borderRadius: 4, textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#555' }}>{transferenciasModal.total}</div>
                <div style={{ fontSize: 10, color: '#888' }}>Total</div>
              </div>
              <div style={{ padding: 8, background: '#F4FBF6', borderRadius: 4, textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#0a7f4a' }}>{transferenciasModal.vinculados}</div>
                <div style={{ fontSize: 10, color: '#888' }}>Vinculadas</div>
              </div>
              <div style={{ padding: 8, background: '#EFF3FF', borderRadius: 4, textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#0930D0' }}>{transferenciasModal.pendentes_com_match_unico}</div>
                <div style={{ fontSize: 10, color: '#888' }}>Match único</div>
              </div>
              <div style={{ padding: 8, background: '#FFF8E8', borderRadius: 4, textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#B07500' }}>{transferenciasModal.pendentes_ambiguos}</div>
                <div style={{ fontSize: 10, color: '#888' }}>Ambíguas (2+)</div>
              </div>
              <div style={{ padding: 8, background: '#FDECEC', borderRadius: 4, textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#B91626' }}>{transferenciasModal.pendentes_sem_candidato}</div>
                <div style={{ fontSize: 10, color: '#888' }}>Sem match</div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {transferenciasModal.items.map((item: any) => (
                <div key={item.cr_id} style={{
                  padding: 12, borderRadius: 6,
                  border: item.ja_vinculado ? '1px solid #B7EB8F'
                    : item.candidatos.length === 1 ? '1px solid #C4D2FF'
                    : item.candidatos.length > 1 ? '1px solid #F0D88E'
                    : '1px solid #E68B96',
                  background: item.ja_vinculado ? '#F4FBF6'
                    : item.candidatos.length === 1 ? '#FAFBFF'
                    : item.candidatos.length > 1 ? '#FFF8E8'
                    : '#FDECEC',
                }}>
                  {/* CR (entrada) */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 14 }}>🟢 ENTRADA (CR)</span>
                    <span style={{ fontWeight: 700, color: '#0a7f4a' }}>+{fmt(item.cr_valor)}</span>
                    <span style={{ color: '#666', fontSize: 11 }}>{fmtData(item.cr_data)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#444', marginBottom: 8 }}>
                    {item.cr_descricao || item.cr_titulo || '(sem descrição)'}
                  </div>

                  {item.ja_vinculado ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, paddingTop: 8, borderTop: '0.5px dashed #B7EB8F' }}>
                        <span style={{ fontSize: 14 }}>🔴 SAÍDA (CP)</span>
                        <span style={{ fontWeight: 700, color: '#B91626' }}>-{fmt(item.cr_valor)}</span>
                        <span style={{ color: '#666', fontSize: 11 }}>{item.cp_data ? fmtData(item.cp_data) : '—'}</span>
                        <button onClick={() => desvincularTransferencia(item.cr_id)}
                          style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 11,
                            background: '#fff', color: '#B91626', border: '0.5px solid #E68B96',
                            borderRadius: 4, cursor: 'pointer' }}>
                          ↺ Desvincular
                        </button>
                      </div>
                      <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                        ✓ {item.cp_descricao}
                      </div>
                    </>
                  ) : item.candidatos.length === 0 ? (
                    <div style={{ fontSize: 11, color: '#B91626', fontStyle: 'italic' }}>
                      Nenhum candidato em CP com mesmo valor + data ± 3 dias. Verifique manualmente.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8,
                      paddingTop: 8, borderTop: '0.5px dashed #ddd' }}>
                      <div style={{ fontSize: 11, color: '#666', fontWeight: 600 }}>
                        Candidato{item.candidatos.length > 1 ? 's' : ''} CP:
                      </div>
                      {item.candidatos.map((c: any) => (
                        <div key={c.id} style={{
                          padding: 8, background: '#fff', borderRadius: 4,
                          border: '0.5px solid #ddd',
                          display: 'flex', alignItems: 'center', gap: 10,
                        }}>
                          <span style={{ fontSize: 12 }}>🔴</span>
                          <span style={{ fontWeight: 700, color: '#B91626', fontSize: 13 }}>-{fmt(Math.abs(c.valor))}</span>
                          <span style={{ color: '#666', fontSize: 11 }}>{fmtData(c.data_competencia || c.data_vencimento)}</span>
                          <div style={{ flex: 1, fontSize: 11, color: '#444' }}>
                            {c.descricao || '(sem descrição)'} {c.conta_financeira_ca && <span style={{ color: '#888' }}>· {c.conta_financeira_ca}</span>}
                          </div>
                          <button onClick={() => vincularTransferencia(item.cr_id, c.id)}
                            style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600,
                              background: '#0930D0', color: '#fff', border: 'none',
                              borderRadius: 4, cursor: 'pointer' }}>
                            🔗 Vincular
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ marginTop: 14, fontSize: 11, color: '#666', fontStyle: 'italic',
              padding: 10, background: '#FAFBFF', borderRadius: 4 }}>
              💡 Pares vinculados aparecem em verde. Match único (azul) e ambíguas (laranja)
              precisam revisão manual. Sem match (vermelho) requer verificação na origem CA.
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* F-CR-merge-amb: wizard de revisão das ambíguas (1 por vez).
          FIN-UI (2026-07-14): portalizado pro body. */}
      {revisandoAmbiguas && (() => {
        const atual = revisandoAmbiguas.parcelas[revisandoAmbiguas.indice];
        if (!atual) return null;
        return createPortal(
          <div onClick={() => setRevisandoAmbiguas(null)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: '#fff', borderRadius: 8, padding: 24, maxWidth: 760,
              width: '90%', maxHeight: '90vh', overflowY: 'auto',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontSize: 16, color: '#020D7F' }}>
                  🔀 Revisar ambíguas — {revisandoAmbiguas.indice + 1} de {revisandoAmbiguas.parcelas.length}
                </h3>
                <button onClick={() => setRevisandoAmbiguas(null)}
                  style={{ marginLeft: 'auto', background: 'transparent', border: 'none',
                    fontSize: 20, color: '#888', cursor: 'pointer' }}>×</button>
              </div>

              {/* Parcela CA */}
              <div style={{ padding: 12, background: '#FFF8E8', borderRadius: 6,
                border: '0.5px solid #F0D88E', marginBottom: 14, fontSize: 12 }}>
                <div style={{ fontWeight: 600, color: '#7A5500', marginBottom: 6 }}>
                  Parcela CA órfã:
                </div>
                <div><strong>{atual.ca.descricao || atual.ca.parcela_titulo || '(sem descrição)'}</strong></div>
                <div style={{ color: '#666', fontSize: 11 }}>
                  {fmt(atual.ca.valor_previsto)} · venc {fmtData(atual.ca.data_vencimento)} ·
                  cliente <strong>{atual.ca.cliente_nome || '—'}</strong> ·
                  status {atual.ca.status}
                </div>
              </div>

              <div style={{ fontSize: 12, color: '#444', marginBottom: 10 }}>
                <strong>{atual.candidatos.length}</strong> candidatos HIGH-score — escolha qual é a mesma parcela:
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {atual.candidatos.map((c: any, i: number) => (
                  <div key={c.id} style={{
                    padding: 12, borderRadius: 6,
                    border: i === 0 ? '1px solid #0a7f4a' : '0.5px solid #ddd',
                    background: i === 0 ? '#F4FBF6' : '#fff',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3,
                          background: i === 0 ? '#0a7f4a' : '#e0e0e0',
                          color: i === 0 ? '#fff' : '#555', fontWeight: 600 }}>
                          score {c.score} {i === 0 && '(top)'}
                        </span>
                        {c.contrato_numero && (
                          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3,
                            background: '#EFF3FF', color: '#0930D0', fontWeight: 600,
                            fontFamily: 'monospace' }}>
                            {c.contrato_numero}
                          </span>
                        )}
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {c.parcela_titulo || c.descricao || '(sem título)'}
                      </div>
                      <div style={{ fontSize: 11, color: '#666' }}>
                        {fmt(c.valor_previsto)} · venc {fmtData(c.data_vencimento)} ·
                        cliente {c.cliente_nome || '—'}
                        {c.contrato_titulo && <> · {c.contrato_titulo.slice(0, 50)}</>}
                      </div>
                    </div>
                    <button onClick={() => aplicarMergeAmbigua(atual.ca.id, c.id)}
                      style={{
                        padding: '8px 16px', fontSize: 12, fontWeight: 600,
                        background: i === 0 ? '#0a7f4a' : '#0930D0', color: '#fff',
                        border: 'none', borderRadius: 4, cursor: 'pointer',
                      }}>
                      🔗 Mesclar
                    </button>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between',
                marginTop: 16, paddingTop: 14, borderTop: '0.5px dashed #ddd' }}>
                <button onClick={() => setRevisandoAmbiguas(null)}
                  style={{ padding: '8px 16px', fontSize: 12, background: '#fff',
                    color: '#555', border: '0.5px solid #ddd', borderRadius: 4,
                    cursor: 'pointer' }}>
                  Pausar (fechar)
                </button>
                <button onClick={proxAmbigua}
                  style={{ padding: '8px 16px', fontSize: 12, fontWeight: 600,
                    background: '#B07500', color: '#fff', border: 'none',
                    borderRadius: 4, cursor: 'pointer' }}>
                  ↳ Pular essa (não mesclar)
                </button>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* Banner: parcelas liberadas pra faturar (Onda 4) */}
      {liberadasCount > 0 && (
        <div style={{
          background: '#F3E8FF', border: '1px solid #C4A1F0', borderRadius: 8,
          padding: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 22 }}>📢</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#5B21B6' }}>
              {liberadasCount} parcela(s) liberada(s) pra faturamento
            </div>
            <div style={{ fontSize: 11, color: '#6B46C1' }}>
              Total {fmt(liberadasValor)} — mobilização autorizada pelo contrato assinado
              ou liberação manual do gestor.
            </div>
          </div>
          <button onClick={() => setFiltroLiberadas(v => !v)}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600,
              background: filtroLiberadas ? '#7C3AED' : '#fff',
              color: filtroLiberadas ? '#fff' : '#7C3AED',
              border: '1px solid #7C3AED', borderRadius: 5, cursor: 'pointer',
            }}>
            {filtroLiberadas ? '✓ Filtrado' : 'Ver só liberadas'}
          </button>
        </div>
      )}

      {/* KPIs estilo Conta Azul — 5 cards horizontais inline */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 0, marginBottom: 14,
        background: '#fff', border: '0.5px solid #e0e0e0',
        borderRadius: 6, overflow: 'hidden',
      }}>
        <KpiCardCR label="Atrasadas (R$)" valor={totalAtrasadas} qtd={qtdAtrasadas} cor="#B91626" />
        <KpiCardCR label="Vencem hoje (R$)" valor={totalVencemHoje} qtd={vencemHoje.length} cor="#E07A1F" borda />
        <KpiCardCR label="A vencer (R$)" valor={totalAVencer} qtd={aVencer.length} cor="#0930D0" borda />
        <KpiCardCR label="Recebidos (R$)" valor={totalRecebido} qtd={qtdRecebido} cor="#0a7f4a" borda />
        <KpiCardCR label="Total do período (R$)" valor={totalPeriodo} qtd={baseKpi.length} cor="#020D7F" borda destacar />
      </div>

      {/* Sprint F11 — Filtros homogeneizados com Contas a Pagar */}
      <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8,
        padding: 12, background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: 6 }}>
        {/* Linha 1: 3-modo + Navegador ◄ ► + 5 presets (sempre há período ativo) */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#666', fontWeight: 600 }}>Por:</span>
          <div style={{ display: 'flex', gap: 0, background: '#F0F1F5', borderRadius: 12, padding: 2 }}>
            {([
              { v: 'vencimento' as const,  label: '📆 Vencimento', title: 'Filtra por data_vencimento (espelha tela do Conta Azul)' },
              { v: 'recebimento' as const, label: '💰 Recebimento', title: 'Filtra por data_recebimento real; em-aberto com vencimento no mês também aparece' },
              { v: 'emissao' as const,     label: '📅 Emissão',     title: 'Filtra por nota_fiscal_emissao (mês fiscal)' },
            ]).map(o => {
              const ativo = campoPeriodo === o.v;
              return (
                <button key={o.v} onClick={() => trocarCampoPeriodo(o.v)}
                  title={o.title}
                  style={{
                    padding: '4px 12px', fontSize: 10, fontWeight: ativo ? 600 : 400,
                    background: ativo ? '#fff' : 'transparent',
                    color: ativo ? '#0930D0' : '#666',
                    border: 'none', borderRadius: 10, cursor: 'pointer',
                    boxShadow: ativo ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                  }}>
                  {o.label}
                </button>
              );
            })}
          </div>
          {/* Navegador ◄ ► — sempre tem período (não há mais estado "sem período"). */}
          {(() => {
            const modoNav = detectarModoNavegador(periodoIni, periodoFim);
            return (
              <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center',
                border: '0.5px solid #d8dcec', borderRadius: 4, background: '#fff' }}>
                <button onClick={() => _navegar(-1)}
                  title="Período anterior"
                  style={{ padding: '5px 10px', fontSize: 14, color: '#0930D0',
                    background: 'transparent', border: 'none', cursor: 'pointer' }}>◄</button>
                <div onClick={() => setPickerCustomAberto(true)}
                  title="Clique para escolher um intervalo personalizado"
                  style={{ minWidth: 140, textAlign: 'center', fontSize: 12, fontWeight: 600,
                    color: '#020D7F', cursor: 'pointer',
                    padding: '3px 10px', textTransform: 'capitalize' }}>
                  {rotuloNavegador(periodoIni, periodoFim)}
                  <span style={{ fontSize: 9, color: '#888', marginLeft: 4 }}>({modoNav})</span>
                </div>
                <button onClick={() => _navegar(1)}
                  title="Próximo período"
                  style={{ padding: '5px 10px', fontSize: 14, color: '#0930D0',
                    background: 'transparent', border: 'none', cursor: 'pointer' }}>►</button>
                <DatePicker.RangePicker
                  open={pickerCustomAberto} onOpenChange={setPickerCustomAberto}
                  value={periodoIni && periodoFim ? [dayjs(periodoIni), dayjs(periodoFim)] : null}
                  format="DD/MM/YYYY" allowClear={false}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setPeriodoIni(dates[0].format('YYYY-MM-DD'));
                      setPeriodoFim(dates[1].format('YYYY-MM-DD'));
                      setPresetAtivo('');
                    }
                    setPickerCustomAberto(false);
                  }}
                  style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%',
                    opacity: 0, pointerEvents: 'none' }} />
              </div>
            );
          })()}
          {PRESETS_PERIODO.map(({ k, fn }) => {
            const ativo = presetAtivo === k;
            return (
              <button key={k} onClick={() => aplicarPreset(fn())}
                style={{
                  padding: '4px 10px', fontSize: 11, borderRadius: 12,
                  background: ativo ? '#0930D0' : '#fff',
                  color: ativo ? '#fff' : '#444',
                  border: `0.5px solid ${ativo ? '#0930D0' : '#ddd'}`,
                  cursor: 'pointer',
                }}>
                {k}
              </button>
            );
          })}
        </div>
        {/* Linha 2: Busca + Conta bancária + Mais filtros + Limpar tudo */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="🔎 Buscar contrato, cliente, descrição, NF..."
            value={busca} onChange={e => setBusca(e.target.value)}
            style={{ flex: 1, minWidth: 240, maxWidth: 380, padding: '7px 10px',
              fontSize: 12, borderRadius: 5, border: '0.5px solid #ddd' }} />
          <select value={filtroContaBancaria} onChange={e => setFiltroContaBancaria(e.target.value)}
            title="Filtrar por conta bancária (só sincronizados da Conta Azul têm essa info)"
            style={{ padding: '7px 10px', fontSize: 12, border: '0.5px solid #ddd',
              borderRadius: 4, background: '#fff', minWidth: 180 }}>
            <option value="">Conta: Todas</option>
            {contasDisponiveis.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={() => setMostrarMaisFiltros(v => !v)}
            style={{ padding: '7px 12px', fontSize: 12, fontWeight: 600,
              background: mostrarMaisFiltros ? '#EFF3FF' : '#fff', color: '#0930D0',
              border: '0.5px solid #d8dcec', borderRadius: 4, cursor: 'pointer' }}>
            Mais filtros {mostrarMaisFiltros ? '▲' : '▼'}
            {(filtrosContrato.length > 0 || filtroCliente || filtroLiberadas) && (
              <span style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px',
                background: '#0930D0', color: '#fff', borderRadius: 3 }}>•</span>
            )}
          </button>
          {(busca || filtrosContrato.length > 0 || filtroCliente || filtroContaBancaria || filtrosStatus.length > 0 || presetAtivo !== 'Este mês' || periodoIni !== presetMesAtual().ini || filtroLiberadas) && (
            <button onClick={() => {
              setBusca(''); setFiltrosContrato([]); setFiltroCliente(''); setFiltroContaBancaria('');
              setFiltrosStatus([]); setFiltroLiberadas(false);
              // Sprint F13 — reset volta pra "Este mês" (paridade com CP).
              aplicarPreset(presetMesAtual());
            }}
              style={{ padding: '6px 10px', fontSize: 11, color: '#0930D0',
                background: 'transparent', border: 'none',
                cursor: 'pointer', textDecoration: 'underline' }}>
              🗑 Limpar filtros
            </button>
          )}
        </div>
        {/* Linha 3 — Status via chips (multi-seleção) */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#666', fontWeight: 600, marginRight: 4 }}>
            Status:
          </span>
          {Object.entries(STATUS_PARC).map(([k, v]) => {
            const ativo = filtrosStatus.includes(k);
            return (
              <button key={k}
                onClick={() => setFiltrosStatus(prev =>
                  ativo ? prev.filter(x => x !== k) : [...prev, k]
                )}
                style={{
                  padding: '4px 10px', fontSize: 11, borderRadius: 12,
                  background: ativo ? v.cor : '#fff',
                  color: ativo ? '#fff' : v.cor,
                  border: `0.5px solid ${v.cor}`,
                  cursor: 'pointer', fontWeight: ativo ? 600 : 400,
                }}>
                {v.label}
              </button>
            );
          })}
          {qtdCongeladas > 0 && (
            <button onClick={() => setMostrarPausados(v => !v)}
              title="Previsões de contratos PAUSADOS ficam congeladas (fora da lista e dos cards) até o contrato voltar. Recebidas e NFs emitidas continuam visíveis."
              style={{ padding: '4px 10px', fontSize: 11, borderRadius: 12,
                background: mostrarPausados ? '#5A5A5A' : '#fff',
                color: mostrarPausados ? '#fff' : '#5A5A5A',
                border: '0.5px solid #5A5A5A', cursor: 'pointer',
                fontWeight: mostrarPausados ? 600 : 400 }}>
              ⏸ Pausados ({qtdCongeladas})
            </button>
          )}
          {filtrosStatus.length > 0 && (
            <button onClick={() => setFiltrosStatus([])}
              style={{ padding: '4px 10px', fontSize: 11, borderRadius: 12,
                background: '#fff', color: '#888', border: '0.5px solid #ddd', cursor: 'pointer' }}>
              ✕ Limpar status ({filtrosStatus.length})
            </button>
          )}
          {/* F-CR-tipo + FIN-UI (2026-07-14): chips de Tipo na MESMA linha dos
              Status (tela full-width comporta; economiza uma faixa vertical).
              Mostra só tipos COM dados no período + os já selecionados. */}
          <span style={{ width: 1, alignSelf: 'stretch', background: '#e0e0e0', margin: '0 8px' }} />
          <span style={{ fontSize: 11, color: '#666', fontWeight: 600, marginRight: 4 }}
            title="Filtre por tipo de receita. Clique em outro chip pra trocar (single-select).">
            Tipo:
          </span>
          {/* Chip "Todos" — reset visual quando nenhum filtro */}
          <button
            onClick={() => {
              setFiltrosTipoReceita([]);
              try { localStorage.setItem(LS_KEY_CR_TIPO_DEFAULT, '[]'); } catch {}
            }}
            style={{
              padding: '4px 10px', fontSize: 11, borderRadius: 12,
              background: filtrosTipoReceita.length === 0 ? '#020D7F' : '#fff',
              color: filtrosTipoReceita.length === 0 ? '#fff' : '#666',
              border: `0.5px solid ${filtrosTipoReceita.length === 0 ? '#020D7F' : '#ddd'}`,
              cursor: 'pointer', fontWeight: filtrosTipoReceita.length === 0 ? 600 : 400,
            }}>
            Todos
          </button>
          {Object.entries(TIPO_RECEITA_LABEL)
            .filter(([k]) => {
              if (filtrosTipoReceita.includes(k)) return true;  // selecionado: sempre mostra
              return parcelasComStatus.some(p =>
                (p.tipo_receita || 'nao_classificada') === k);  // tem dado: mostra
            })
            .map(([k, v]) => {
            const ativo = filtrosTipoReceita.includes(k);
            return (
              <button key={k}
                onClick={() => {
                  // Single-select: clica num tipo → SO ele fica marcado.
                  // Re-clica no mesmo → volta pra "Todos" (limpa).
                  const novo = ativo ? [] : [k];
                  setFiltrosTipoReceita(novo);
                  try { localStorage.setItem(LS_KEY_CR_TIPO_DEFAULT, JSON.stringify(novo)); } catch {}
                }}
                style={{
                  padding: '4px 10px', fontSize: 11, borderRadius: 12,
                  background: ativo ? v.cor : v.bg,
                  color: ativo ? '#fff' : v.cor,
                  border: `0.5px solid ${v.cor}`,
                  cursor: 'pointer', fontWeight: ativo ? 600 : 400,
                }}>
                {v.icon} {v.label}
              </button>
            );
          })}
          <span style={{ fontSize: 10, color: '#aaa', marginLeft: 'auto', fontStyle: 'italic' }}>
            💾 sua seleção fica salva
          </span>
        </div>
        {/* Linha 4 (condicional) — Mais filtros: contrato, range manual, liberadas */}
        {mostrarMaisFiltros && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap',
            paddingTop: 10, borderTop: '0.5px dashed #eee' }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: '#555',
                textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>
                Contrato <span style={{ fontWeight: 400, textTransform: 'none', color: '#888' }}>
                  — dá pra escolher vários; "TUDO" traz o contrato com os aditivos
                </span>
              </label>
              <Select
                mode="multiple"
                showSearch
                value={filtrosContrato}
                onChange={(v) => setFiltrosContrato(v as string[])}
                placeholder={`Todos (${totalContratosNoFiltro})`}
                allowClear
                size="small"
                maxTagCount="responsive"
                style={{ minWidth: 300, maxWidth: 520 }}
                // busca por número E título (case-insensitive). Com opções
                // agrupadas o antd só chama isto nas folhas — o "TUDO" carrega
                // o número no rótulo justamente pra ser encontrado na busca.
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={opcoesContrato}
              />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: '#555',
                textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>
                Cliente
              </label>
              <Select
                showSearch
                value={filtroCliente || undefined}
                onChange={(v) => setFiltroCliente(v || '')}
                placeholder={`Todos (${clientesDisponiveis.length})`}
                allowClear
                size="small"
                style={{ width: 280 }}
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[
                  { value: '__SEM__', label: '— Sem cliente —' },
                  ...clientesDisponiveis.map(c => ({ value: c, label: c })),
                ]}
              />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: '#555',
                textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>De</label>
              <input type="date" value={periodoIni}
                onChange={e => { setPeriodoIni(e.target.value); setPresetAtivo(''); }}
                style={{ padding: '7px 10px', fontSize: 12, borderRadius: 4, border: '0.5px solid #ddd' }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: '#555',
                textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>Até</label>
              <input type="date" value={periodoFim}
                onChange={e => { setPeriodoFim(e.target.value); setPresetAtivo(''); }}
                style={{ padding: '7px 10px', fontSize: 12, borderRadius: 4, border: '0.5px solid #ddd' }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
              color: '#555', cursor: 'pointer', paddingBottom: 8 }}>
              <input type="checkbox" checked={filtroLiberadas}
                onChange={e => setFiltroLiberadas(e.target.checked)} />
              Só liberadas pra faturar
            </label>
          </div>
        )}
        {/* Indicador de filtro ativo */}
        {(presetAtivo || periodoIni || filtrosContrato.length > 0 || filtroCliente || filtroAtrasadas || filtroContaBancaria || filtrosStatus.length > 0 || filtrosTipoReceita.length > 0 || busca) && (
          <div style={{ fontSize: 11, color: '#0930D0' }}>
            Mostrando <strong>{filtradas.length}</strong> de {parcelasComStatus.length} parcelas
            {presetAtivo && ` · período (${campoPeriodo}): ${presetAtivo}`}
            {!presetAtivo && (periodoIni || periodoFim) && ` · período (${campoPeriodo})`}
            {filtrosContrato.length > 0 && ` · ${filtrosContrato.length === 1 ? '1 contrato' : filtrosContrato.length + ' contratos'} no filtro`}
            {familiaDoUnico && (
              <> {' '}
                <button onClick={() => setFiltrosContrato([valorDaFamilia(familiaDoUnico.raizId)])}
                  title="Mostrar também as parcelas dos aditivos deste contrato"
                  style={{ background: 'none', border: 'none', color: '#0930D0', fontWeight: 600,
                    fontSize: 11, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                  + incluir os {familiaDoUnico.membros.length - 1} aditivos
                </button>
              </>
            )}
            {filtroCliente && ` · cliente: ${filtroCliente === '__SEM__' ? '— sem cliente —' : filtroCliente}`}
            {filtroContaBancaria && ` · conta: ${filtroContaBancaria}`}
            {filtroAtrasadas && ` · só atrasadas`}
            {filtrosStatus.length > 0 && ` · status: ${filtrosStatus.map(s => STATUS_PARC[s]?.label || s).join(' OU ')}`}
            {filtrosTipoReceita.length > 0 && (
              <> · tipo: {filtrosTipoReceita.map(t => TIPO_RECEITA_LABEL[t]?.label || t).join(' OU ')}
                {filtrosTipoReceita.length > 1 && (
                  <> {' '}
                    <button onClick={() => { setFiltrosTipoReceita(['nao_classificada']); }}
                      title="Filtrar só as Não classificadas"
                      style={{ background: 'none', border: 'none', color: '#B91626', fontWeight: 600,
                        fontSize: 11, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                      → ver só Não classificadas
                    </button>
                  </>
                )}
              </>
            )}
            {busca && ` · busca: "${busca}"`}
            {filtradas.length === 0 && (busca || filtrosContrato.length > 0 || filtroCliente || filtroContaBancaria) && (
              <> · <button onClick={() => {
                setBusca(''); setFiltrosContrato([]); setFiltroCliente(''); setFiltroContaBancaria(''); setFiltroAtrasadas(false);
              }} style={{ background: 'none', border: 'none', color: '#B91626', fontWeight: 600,
                fontSize: 11, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                🗑 limpar filtros de texto pra ver os resultados
              </button></>
            )}
          </div>
        )}
      </div>

      {/* Sprint F6/F8 — dicas de reordenação + resetar colunas */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 8, fontSize: 11, color: '#666', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span title="Clique no nome da coluna pra ordenar (↑ asc / ↓ desc). Clique de novo pra inverter.">
            💡 Clique no <strong style={{ color: '#0930D0' }}>nome da coluna</strong> pra ordenar
            · Arraste o <strong style={{ color: '#0930D0' }}>⋮⋮</strong> pra reordenar a coluna na tabela.
          </span>
          {contratoUnico && parcelasRender.length > 1 && ordenacao.coluna === 'contrato' && ordenacao.direcao === 'asc' && (
            <span title="Com sort por Contrato (asc) + filtro de contrato, arrastar linhas reordena parcelas">
              · 🔃 Arraste as <strong style={{ color: '#0930D0' }}>linhas</strong> pra reordenar parcelas deste contrato.
            </span>
          )}
          {salvandoOrdem && <span style={{ color: '#0930D0', fontWeight: 600 }}>Salvando ordem…</span>}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {/* Indicador de sort ativo + botão limpar pra default */}
          <span style={{ fontSize: 10, color: '#888' }}>
            Ordenado por: <strong>{COL_DEFS[ordenacao.coluna]?.label}</strong> {ordenacao.direcao === 'asc' ? '↑' : '↓'}
          </span>
          {(ordenacao.coluna !== 'emissao' || ordenacao.direcao !== 'desc') && (
            <button onClick={() => {
              const nova = { coluna: 'emissao' as ColunaKey, direcao: 'desc' as DirecaoSort };
              setOrdenacao(nova);
              try { localStorage.setItem(LS_KEY_ORDENACAO, JSON.stringify(nova)); } catch { /* ignore */ }
            }}
              style={{ padding: '3px 9px', fontSize: 10, background: 'transparent',
                color: '#888', border: '0.5px solid #ddd', borderRadius: 3, cursor: 'pointer' }}>
              ↻ Sort: emissão ↓
            </button>
          )}
          {/* Verifica se a ordem das colunas difere do default */}
          {JSON.stringify(ordemColunas) !== JSON.stringify(COLUNAS_DEFAULT) && (
            <button onClick={resetarColunas}
              style={{ padding: '3px 9px', fontSize: 10, background: 'transparent',
                color: '#888', border: '0.5px solid #ddd', borderRadius: 3, cursor: 'pointer' }}>
              ↻ Resetar ordem das colunas
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Carregando...</div>
      ) : parcelasRender.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#aaa', fontStyle: 'italic',
          border: '0.5px dashed #ddd', borderRadius: 6 }}>
          {parcelasComStatus.length === 0
            ? 'Nenhuma parcela cadastrada. Crie em Operacional → Projetos → Financeiro (resumo).'
            : 'Nenhuma parcela corresponde ao filtro.'}
        </div>
      ) : (
        <>
        {/* Agrupar NF: barra do modo agrupar (aglutinar N parcelas em 1 NF / desagrupar) */}
        {modoAgrupar && (() => {
          const gruposNaSelecao = Array.from(new Set(
            parcelas.filter(p => selGrupo.has(p.id) && p.nf_grupo_id).map(p => p.nf_grupo_id as string)));
          const algumaAgrupada = gruposNaSelecao.length > 0;
          const podeAgrupar = selGrupo.size >= 2 && !algumaAgrupada;
          return (
            <div style={{
              padding: '10px 14px', background: '#E8F5EE', borderRadius: 6,
              marginBottom: 10, display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#0a7f4a' }}>
                🔗 Modo agrupar NF — marque 2+ parcelas (pode cruzar aditivos/contratos) · {selGrupo.size} selecionada{selGrupo.size !== 1 ? 's' : ''}
                {algumaAgrupada && ' · algumas já estão em grupo'}
              </span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {algumaAgrupada && (
                  <button onClick={() => desagruparNf(gruposNaSelecao)} disabled={agrupando}
                    style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600,
                      background: '#fff', color: '#B91626', border: '1px solid #B91626',
                      borderRadius: 4, cursor: agrupando ? 'wait' : 'pointer' }}>
                    {agrupando ? '…' : 'Desagrupar'}
                  </button>
                )}
                <button onClick={agruparNf} disabled={agrupando || !podeAgrupar}
                  title={algumaAgrupada ? 'Desagrupe as que já estão em grupo antes de reagrupar' : ''}
                  style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600,
                    background: (agrupando || !podeAgrupar) ? '#9bbfa9' : '#0a7f4a',
                    color: '#fff', border: 'none', borderRadius: 4,
                    cursor: (agrupando || !podeAgrupar) ? 'not-allowed' : 'pointer' }}>
                  {agrupando ? 'Agrupando…' : 'Agrupar em 1 NF'}
                </button>
                <button onClick={() => { setModoAgrupar(false); setSelGrupo(new Set()); }}
                  style={{ padding: '6px 12px', fontSize: 12,
                    background: '#fff', color: '#555', border: '0.5px solid #ddd',
                    borderRadius: 4, cursor: 'pointer' }}>
                  Cancelar
                </button>
              </div>
            </div>
          );
        })()}
        {/* F-CR-merge-bulk: barra de seleção pra bulk merge (só quando há CA órfãs selecionadas) */}
        {selecionadasMerge.size > 0 && (
          <div style={{
            padding: '10px 14px', background: '#E8EEFF', borderRadius: 6,
            marginBottom: 10, display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#020D7F' }}>
              🔗 {selecionadasMerge.size} parcela{selecionadasMerge.size !== 1 ? 's' : ''} CA selecionada{selecionadasMerge.size !== 1 ? 's' : ''} pra mesclar
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={preverBulkMerge} disabled={aplicandoBulk}
                style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600,
                  background: '#0930D0', color: '#fff', border: 'none',
                  borderRadius: 4, cursor: aplicandoBulk ? 'wait' : 'pointer' }}>
                {aplicandoBulk ? 'Analisando…' : 'Analisar e mesclar'}
              </button>
              <button onClick={limparSelecaoMerge}
                style={{ padding: '6px 12px', fontSize: 12,
                  background: '#fff', color: '#555', border: '0.5px solid #ddd',
                  borderRadius: 4, cursor: 'pointer' }}>
                Limpar seleção
              </button>
            </div>
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, background: '#fff',
          border: '0.5px solid #e0e0e0', borderRadius: 6 }}>
          {/* FIN-UI (2026-07-14): cabeçalho sticky (padrão AbaFinanceiro) — não some ao rolar */}
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr style={{ background: '#F0F1F5', color: '#555' }}>
              {/* checkbox header (merge CA órfãs OU modo agrupar NF) */}
              {(filtradas.some(caOrfaSelecionavel) || modoAgrupar) && (
                <th style={{ padding: '10px 6px 10px 12px', width: 36 }}>
                  {!modoAgrupar && (
                    <input type="checkbox"
                      checked={selecionadasMerge.size > 0
                        && filtradas.filter(caOrfaSelecionavel).every(p => selecionadasMerge.has(p.id))}
                      onChange={(e) => e.target.checked ? selecionarTodasOrfas() : limparSelecaoMerge()}
                      title="Selecionar todas as parcelas CA órfãs visíveis pra mesclar" />
                  )}
                </th>
              )}
              {ordemColunas.map((k) => {
                const def = COL_DEFS[k];
                const arrastando = dragColuna === k;
                const sortavel = COLUNAS_SORTAVEIS.includes(k);
                const ativoSort = ordenacao.coluna === k;
                // Sprint F8.1: click no <th> ordena; drag pelo handle `⋮⋮` reordena coluna.
                // Pra não disparar sort ao soltar drag, só dispara onClick se dragColuna ficar null.
                return (
                  <th key={k}
                    onClick={() => { if (!dragColuna && sortavel) aplicarSort(k); }}
                    style={{
                      ...def.thStyle,
                      cursor: sortavel ? 'pointer' : 'default',
                      background: arrastando ? '#D8E0FF' : ativoSort ? '#E8EEFF' : undefined,
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                    }}>
                    {/* Handle de drag — único elemento draggable */}
                    <span
                      draggable
                      onDragStart={(e) => { setDragColuna(k); e.dataTransfer.effectAllowed = 'move'; e.stopPropagation(); }}
                      onDragOver={(e) => { if (dragColuna && dragColuna !== k) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
                      onDrop={(e) => { e.stopPropagation(); onDropColuna(k); }}
                      onDragEnd={() => setDragColuna(null)}
                      onClick={(e) => e.stopPropagation()}
                      title="Arraste pra reordenar coluna"
                      style={{ opacity: 0.4, marginRight: 4, cursor: 'grab', display: 'inline-block', padding: '0 2px' }}>
                      ⋮⋮
                    </span>
                    {def.label}
                    {ativoSort && (
                      <span style={{ marginLeft: 4, color: '#0930D0', fontWeight: 700 }}>
                        {ordenacao.direcao === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                    {sortavel && !ativoSort && (
                      <span style={{ marginLeft: 4, color: '#bbb', fontSize: 9 }}>↕</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {parcelasRender.map(p => {
              const isAtrasada = p.status === 'atrasada';
              // Sprint F8.1: drag-de-linhas só ativo quando filtra por contrato
              // E ordena por "contrato asc" (caso contrário arrastar não faz sentido
              // visual porque a ordem é determinada pelo sort).
              const dndAtivo = !!contratoUnico
                && ordenacao.coluna === 'contrato'
                && ordenacao.direcao === 'asc';
              return (
                <tr key={p.id}
                  draggable={dndAtivo}
                  onDragStart={(e) => { if (dndAtivo) { setDragId(p.id); e.dataTransfer.effectAllowed = 'move'; } }}
                  onDragOver={(e) => { if (dndAtivo && dragId && dragId !== p.id) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
                  onDrop={() => onDropParcela(p.id)}
                  onDragEnd={() => setDragId(null)}
                  onClick={() => { if (!dragId) setParcelaAberta(p); }}
                  style={{ borderBottom: '0.5px solid #f0f0f0',
                  background: selGrupo.has(p.id) ? '#E8F5EE'
                    : selecionadasMerge.has(p.id) ? '#E8EEFF'
                    : dragId === p.id ? '#EFF3FF'
                    : isAtrasada ? '#FEF7F7' : 'transparent',
                  opacity: salvandoOrdem ? 0.6 : 1,
                  cursor: dndAtivo ? 'grab' : 'pointer' }}>
                  {/* checkbox por linha (merge CA órfãs OU modo agrupar NF) */}
                  {(filtradas.some(caOrfaSelecionavel) || modoAgrupar) && (
                    <td style={{ padding: '10px 6px 10px 12px', verticalAlign: 'middle' }}
                      onClick={(e) => e.stopPropagation()}>
                      {modoAgrupar
                        ? (podeAgruparNf(p) ? (
                            <input type="checkbox" checked={selGrupo.has(p.id)}
                              onChange={() => toggleSelGrupo(p.id)} />
                          ) : null)
                        : (caOrfaSelecionavel(p) ? (
                            <input type="checkbox" checked={selecionadasMerge.has(p.id)}
                              onChange={() => toggleSelecaoMerge(p.id)} />
                          ) : null)}
                    </td>
                  )}
                  {ordemColunas.map((k) => {
                    const def = COL_DEFS[k];
                    return (
                      <td key={k} style={{
                        padding: 10,
                        fontSize: 11,
                        textAlign: (def.thStyle.textAlign as any) || 'left',
                      }}>
                        {def.render(p)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        </>
      )}

      {/* F-CR-merge-bulk: modal de preview/confirmação do bulk merge.
          FIN-UI (2026-07-14): portalizado pro body. */}
      {previewBulkMerge && createPortal(
        <div onClick={() => setPreviewBulkMerge(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 8, padding: 24, maxWidth: 720,
            width: '90%', maxHeight: '85vh', overflowY: 'auto',
          }}>
            <h3 style={{ margin: '0 0 14px', color: '#020D7F', fontSize: 16 }}>
              🔗 Mesclar em lote — preview
            </h3>
            <div style={{ marginBottom: 14, padding: 12, background: '#FAFBFF',
              borderRadius: 6, border: '0.5px solid #ddd' }}>
              <div style={{ fontSize: 12, color: '#444', marginBottom: 8 }}>
                <strong>{previewBulkMerge.total_avaliadas}</strong> parcelas CA analisadas com critério HIGH-score (≥ {previewBulkMerge.score_min}):
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                <div style={{ padding: 10, background: '#F4FBF6', borderRadius: 5, border: '0.5px solid #B7EB8F' }}>
                  <div style={{ color: '#0a7f4a', fontWeight: 700, fontSize: 22 }}>{previewBulkMerge.mescladas}</div>
                  <div style={{ fontSize: 11, color: '#555' }}>Vão ser mescladas (1 candidato HIGH)</div>
                </div>
                <div style={{ padding: 10, background: '#FFF8E8', borderRadius: 5, border: '0.5px solid #F0D88E' }}>
                  <div style={{ color: '#B07500', fontWeight: 700, fontSize: 22 }}>{previewBulkMerge.ambiguas}</div>
                  <div style={{ fontSize: 11, color: '#555' }}>Ambíguas (2+ HIGH — pra revisar manual)</div>
                </div>
                <div style={{ padding: 10, background: '#FDECEC', borderRadius: 5, border: '0.5px solid #E68B96' }}>
                  <div style={{ color: '#B91626', fontWeight: 700, fontSize: 22 }}>{previewBulkMerge.sem_candidato}</div>
                  <div style={{ fontSize: 11, color: '#555' }}>Sem candidato (classifique pelo tipo)</div>
                </div>
                <div style={{ padding: 10, background: '#F2F2F4', borderRadius: 5, border: '0.5px solid #ddd' }}>
                  <div style={{ color: '#888', fontWeight: 700, fontSize: 22 }}>{previewBulkMerge.erros}</div>
                  <div style={{ fontSize: 11, color: '#555' }}>Erros</div>
                </div>
              </div>
            </div>

            {previewBulkMerge.amostra_mescladas?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#666', fontWeight: 600, marginBottom: 4 }}>
                  Amostra do que será mesclado:
                </div>
                <div style={{ fontSize: 11, lineHeight: 1.6 }}>
                  {previewBulkMerge.amostra_mescladas.map((a: any, i: number) => (
                    <div key={i} style={{ padding: '4px 8px', background: '#F4FBF6', borderRadius: 3, marginBottom: 3 }}>
                      ✓ <strong>{a.descricao}</strong>
                      {a.contrato && <> → contrato {a.contrato}</>}
                      <span style={{ color: '#888' }}> (score {a.score})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {previewBulkMerge.amostra_ambiguas?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#B07500', fontWeight: 600, marginBottom: 4 }}>
                  Amostra de ambíguas (não vão ser mescladas):
                </div>
                <div style={{ fontSize: 11, lineHeight: 1.6 }}>
                  {previewBulkMerge.amostra_ambiguas.map((a: any, i: number) => (
                    <div key={i} style={{ padding: '4px 8px', background: '#FFF8E8', borderRadius: 3, marginBottom: 3 }}>
                      ⚠ <strong>{a.ca_descricao}</strong>
                      <span style={{ color: '#888' }}> ({a.n_candidatos_high} candidatos HIGH)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button onClick={() => setPreviewBulkMerge(null)} disabled={aplicandoBulk}
                style={{ padding: '8px 16px', fontSize: 12, background: '#fff',
                  color: '#555', border: '0.5px solid #ddd', borderRadius: 4,
                  cursor: aplicandoBulk ? 'wait' : 'pointer' }}>
                Cancelar
              </button>
              <button onClick={aplicarBulkMerge} disabled={aplicandoBulk || previewBulkMerge.mescladas === 0}
                style={{ padding: '8px 20px', fontSize: 12, fontWeight: 600,
                  background: previewBulkMerge.mescladas === 0 ? '#ccc' : '#0a7f4a',
                  color: '#fff', border: 'none', borderRadius: 4,
                  cursor: aplicandoBulk ? 'wait' : 'pointer' }}>
                {aplicandoBulk
                  ? `Aplicando… ${progressoBulk}`
                  : `✓ Aplicar — mesclar ${previewBulkMerge.mescladas}`}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {emitindoNf && (
        <ModalEmitirNf parcela={emitindoNf}
          onClose={() => setEmitindoNf(null)}
          onSucesso={() => { setEmitindoNf(null); carregar(true); }} />
      )}

      {linkBm && (
        <ModalLinkBm link={linkBm} onClose={() => setLinkBm(null)} />
      )}

      {/* Sprint F1: modal rico de detalhe da parcela */}
      {parcelaAberta && (
        <ModalParcela parcela={parcelaAberta}
          abaInicial={abaInicialModal || undefined}
          onClose={fecharModalParcela}
          onMudou={() => { carregar(true); }} />
      )}

      {/* Lançamento avulso — recebível sem contrato (paridade com Contas a Pagar) */}
      {mostrarNovaConta && (
        <ModalNovaContaReceber
          onFechar={() => setMostrarNovaConta(false)}
          onCriou={() => { setMostrarNovaConta(false); carregar(true); }} />
      )}
    </div>
  );
}
